import React, { useState, lazy, Suspense, useEffect, useRef, useCallback } from 'react';
import GameFeedback from './GameFeedback';
import { Routes, Route, useNavigate, useParams, useLocation } from 'react-router-dom';
// Not lazy-loaded: it is tiny and must exist the instant any page renders.
import FeedbackWidget from './components/FeedbackWidget';
import RAMAYANA_MOVIE from './ramayanaMovie.json';
import RAMAYANA_GAMES from './ramayanaGames.json';
import RAMAYANA_CHARACTERS from './ramayanaCharacters.json';

// Lazy-load all game components
const RamaArchery = lazy(() => import('./RamaArchery'));
const LankaBattle = lazy(() => import('./LankaBattle'));
const LankaDash = lazy(() => import('./LankaDash'));
const GoldenDeerChase = lazy(() => import('./GoldenDeerChase'));
const Chakravyuha = lazy(() => import('./Chakravyuha'));
const SamudraManthan = lazy(() => import('./SamudraManthan'));
const Dashavatara = lazy(() => import('./Dashavatara'));
const ShakuniDice = lazy(() => import('./ShakuniDice'));
const Astra = lazy(() => import('./Astra'));
const MakhanChor = lazy(() => import('./MakhanChor'));
const MantraMayhem = lazy(() => import('./MantraMayhem'));
const SutraSpeed = lazy(() => import('./SutraSpeed'));
const DevaMatch = lazy(() => import('./DevaMatch'));
const NavagrahaNavigator = lazy(() => import('./NavagrahaNavigator'));
const RishisGarden = lazy(() => import('./RishisGarden'));
const GitaWisdomTower = lazy(() => import('./GitaWisdomTower'));
const SandhiSmasher = lazy(() => import('./SandhiSmasher'));
const DhammapadaDaily = lazy(() => import('./DhammapadaDaily'));
const VahanaVerse = lazy(() => import('./VahanaVerse'));
const DharmaCourt = lazy(() => import('./DharmaCourt'));
const WheelOfSamsara = lazy(() => import('./WheelOfSamsara'));
const RamayanaHub = lazy(() => import('./ramayana/RamayanaHub'));
const RamayanaChapterPlayer = lazy(() => import('./ramayana/RamayanaHub').then(m => ({ default: m.RamayanaChapterPlayer })));
const PalmReading = lazy(() => import('./PalmReading'));
const Kurukshetra = lazy(() => import('./Kurukshetra'));
const KurukshetraLines = lazy(() => import('./KurukshetraLines'));
const HanumansLeap = lazy(() => import('./HanumansLeap'));
const KurukshetraFPV = lazy(() => import('./KurukshetraFPV'));
const KurukshetraDuty = lazy(() => import('./KurukshetraDuty'));
const RamayanaMovieQuiz = lazy(() => import('./RamayanaMovieQuiz'));
const WhichRamayanaCharacter = lazy(() => import('./WhichRamayanaCharacter'));
const TatakaTakedown = lazy(() => import('./TatakaTakedown'));
const StringShivasBow = lazy(() => import('./StringShivasBow'));
const LakshmanRekha = lazy(() => import('./LakshmanRekha'));
const SpotTheRealDeer = lazy(() => import('./SpotTheRealDeer'));
const KumbhakarnaWakeUp = lazy(() => import('./KumbhakarnaWakeUp'));
const VibhishanaChoice = lazy(() => import('./VibhishanaChoice'));
const ShivaTandava = lazy(() => import('./ShivaTandava'));

// ─── Analytics Helper ──────────────────────────────────────────────
const analytics = {
  pageView(pageName, pagePath) {
    if (window.vedicAnalytics) window.vedicAnalytics.pageView(pageName, pagePath);
  },
  gameStart(gameId, gameName) {
    if (window.vedicAnalytics) window.vedicAnalytics.gameStart(gameId, gameName);
  },
  gameEnd(gameId, gameName, durationSec, score, outcome) {
    if (window.vedicAnalytics) window.vedicAnalytics.gameEnd(gameId, gameName, durationSec, score, outcome);
  },
  engagement(action, label, value) {
    if (window.vedicAnalytics) window.vedicAnalytics.engagement(action, label, value);
  },
  event(action, category, label, value) {
    if (window.gtag) {
      window.gtag('event', action, {
        event_category: category,
        event_label: label,
        value: value,
      });
    }
  },
};

// ─── Typography Constants ────────────────────────────────────────
const FONT = {
  heading: "'Cinzel', Georgia, serif",
  body: "'Inter', system-ui, sans-serif",
  accent: "'Tiro Devanagari', serif",
};

// ─── SVG Icons ─────────────────────────────────────────────────────
function ArcheryIcon({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <path d="M20 12 C8 28, 8 36, 20 52" stroke="#f5c518" strokeWidth="3" fill="none" strokeLinecap="round"/>
      <line x1="20" y1="12" x2="20" y2="52" stroke="#e8d5b7" strokeWidth="1.5"/>
      <line x1="18" y1="32" x2="56" y2="32" stroke="#c9a648" strokeWidth="2.5" strokeLinecap="round"/>
      <polygon points="56,32 50,28 50,36" fill="#ddd"/>
      <polygon points="20,32 26,28 26,32" fill="#cc2936"/>
      <polygon points="20,32 26,36 26,32" fill="#cc2936"/>
    </svg>
  );
}

function TowerIcon({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <rect x="18" y="20" width="28" height="34" rx="2" fill="#2a1810"/>
      <rect x="14" y="16" width="8" height="8" rx="1" fill="#FF4500"/>
      <rect x="28" y="16" width="8" height="8" rx="1" fill="#FF4500"/>
      <rect x="42" y="16" width="8" height="8" rx="1" fill="#FF4500"/>
      <path d="M27 54 L27 40 Q32 35 37 40 L37 54" fill="#FF6B35"/>
      <line x1="32" y1="16" x2="32" y2="6" stroke="#FFD700" strokeWidth="1.5"/>
      <polygon points="32,6 42,10 32,14" fill="#FFD700" opacity="0.8"/>
      <circle cx="24" cy="30" r="2.5" fill="rgba(255,200,50,0.4)"/>
      <circle cx="40" cy="30" r="2.5" fill="rgba(255,200,50,0.4)"/>
    </svg>
  );
}

function RunnerIcon({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <circle cx="28" cy="18" r="8" fill="#d4884a"/>
      <polygon points="22,12 25,4 28,10 31,3 34,12" fill="#FFD700"/>
      <ellipse cx="30" cy="30" rx="7" ry="10" fill="#d4884a"/>
      <line x1="26" y1="38" x2="18" y2="52" stroke="#d4884a" strokeWidth="4" strokeLinecap="round"/>
      <line x1="34" y1="38" x2="44" y2="48" stroke="#d4884a" strokeWidth="4" strokeLinecap="round"/>
      <line x1="24" y1="26" x2="14" y2="22" stroke="#d4884a" strokeWidth="3.5" strokeLinecap="round"/>
      <line x1="36" y1="26" x2="44" y2="20" stroke="#d4884a" strokeWidth="3.5" strokeLinecap="round"/>
      <ellipse cx="16" cy="36" rx="6" ry="4" fill="#FF4500" opacity="0.8"/>
      <ellipse cx="12" cy="34" rx="4" ry="3" fill="#FFD700" opacity="0.7"/>
    </svg>
  );
}

function DeerIcon({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <ellipse cx="32" cy="34" rx="14" ry="9" fill="#c9a648"/>
      <ellipse cx="44" cy="24" rx="5" ry="8" fill="#c9a648" transform="rotate(-15 44 24)"/>
      <circle cx="48" cy="16" r="6" fill="#d4b050"/>
      <path d="M46 12 L42 4 L40 8" stroke="#8B6914" strokeWidth="2" fill="none" strokeLinecap="round"/>
      <path d="M50 11 L52 3 L54 7" stroke="#8B6914" strokeWidth="2" fill="none" strokeLinecap="round"/>
      <circle cx="50" cy="15" r="1.5" fill="#1a0a00"/>
      <line x1="24" y1="40" x2="22" y2="54" stroke="#a88838" strokeWidth="3" strokeLinecap="round"/>
      <line x1="30" y1="42" x2="28" y2="54" stroke="#a88838" strokeWidth="3" strokeLinecap="round"/>
      <line x1="36" y1="42" x2="38" y2="54" stroke="#a88838" strokeWidth="3" strokeLinecap="round"/>
      <line x1="40" y1="40" x2="42" y2="54" stroke="#a88838" strokeWidth="3" strokeLinecap="round"/>
      <circle cx="32" cy="34" r="18" fill="none" stroke="#FFD700" strokeWidth="1" opacity="0.3"/>
    </svg>
  );
}

function MazeIcon({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <circle cx="32" cy="32" r="28" stroke="#c9a648" strokeWidth="1.5" fill="none" opacity="0.4"/>
      <circle cx="32" cy="32" r="22" stroke="#c9a648" strokeWidth="1.5" fill="none" opacity="0.5"/>
      <circle cx="32" cy="32" r="16" stroke="#c9a648" strokeWidth="1.5" fill="none" opacity="0.6"/>
      <circle cx="32" cy="32" r="10" stroke="#c9a648" strokeWidth="1.5" fill="none" opacity="0.7"/>
      <circle cx="32" cy="32" r="4" fill="#4fc3f7"/>
      <line x1="32" y1="4" x2="32" y2="10" stroke="#1a0f0a" strokeWidth="3"/>
      <line x1="54" y1="32" x2="48" y2="32" stroke="#1a0f0a" strokeWidth="3"/>
      <line x1="16" y1="20" x2="22" y2="20" stroke="#1a0f0a" strokeWidth="3"/>
      <line x1="38" y1="48" x2="38" y2="54" stroke="#1a0f0a" strokeWidth="3"/>
    </svg>
  );
}

function ChurnIcon({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <ellipse cx="32" cy="50" rx="28" ry="8" fill="#0a3d6b" opacity="0.5"/>
      <rect x="28" y="14" width="8" height="30" fill="#c9a648" rx="2"/>
      <polygon points="26,14 38,14 34,6 30,6" fill="#f5c518"/>
      <path d="M12 34 Q22 30, 28 34" stroke="#2ecc71" strokeWidth="2.5" fill="none"/>
      <path d="M36 34 Q42 30, 52 34" stroke="#2ecc71" strokeWidth="2.5" fill="none"/>
      <ellipse cx="32" cy="52" rx="8" ry="4" fill="#4a7c59"/>
      <circle cx="32" cy="10" r="3" fill="#FFD700"/>
    </svg>
  );
}

function AvatarIcon({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <circle cx="32" cy="20" r="10" fill="#3f51b5"/>
      <polygon points="26,12 29,4 32,10 35,4 38,12" fill="#FFD700"/>
      <ellipse cx="32" cy="38" rx="10" ry="12" fill="#3f51b5"/>
      <circle cx="32" cy="32" r="22" stroke="#9b59b6" strokeWidth="1" fill="none" opacity="0.3" strokeDasharray="4 4"/>
      <text x="32" y="56" textAnchor="middle" fill="#f5c518" fontSize="10" fontFamily="serif">X</text>
    </svg>
  );
}

function DiceIcon({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <rect x="12" y="12" width="40" height="40" rx="6" fill="#2a1210" stroke="#d4a12a" strokeWidth="2"/>
      <circle cx="24" cy="24" r="3" fill="#f0e6d3"/>
      <circle cx="40" cy="24" r="3" fill="#f0e6d3"/>
      <circle cx="32" cy="32" r="3" fill="#f0e6d3"/>
      <circle cx="24" cy="40" r="3" fill="#f0e6d3"/>
      <circle cx="40" cy="40" r="3" fill="#f0e6d3"/>
    </svg>
  );
}

function CardIcon({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <rect x="8" y="8" width="32" height="44" rx="4" fill="#1a1a2e" stroke="#e74c3c" strokeWidth="2"/>
      <rect x="24" y="12" width="32" height="44" rx="4" fill="#1a1a2e" stroke="#3498db" strokeWidth="2"/>
      <text x="40" y="40" textAnchor="middle" fill="#9b59b6" fontSize="16" fontFamily="serif" fontWeight="bold">A</text>
      <circle cx="16" cy="22" r="4" fill="#e74c3c" opacity="0.7"/>
      <polygon points="40,18 43,24 37,24" fill="#3498db" opacity="0.7"/>
    </svg>
  );
}

function ButterIcon({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <ellipse cx="32" cy="28" rx="16" ry="12" fill="#c9784a"/>
      <ellipse cx="32" cy="24" rx="12" ry="6" fill="#ffc107"/>
      <ellipse cx="32" cy="22" rx="8" ry="3" fill="#fff3b0" opacity="0.6"/>
      <circle cx="32" cy="14" r="8" fill="#3f51b5"/>
      <polygon points="28,8 30,2 32,7 34,2 36,8" fill="#FFD700" />
      <path d="M26 14 Q24 18, 26 22" stroke="#3f51b5" strokeWidth="2" fill="none"/>
      <path d="M38 14 Q40 18, 38 22" stroke="#3f51b5" strokeWidth="2" fill="none"/>
    </svg>
  );
}

function MantraIcon({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <rect x="8" y="12" width="48" height="40" rx="4" fill="#2a1810" stroke="#FF6B1A" strokeWidth="1.5"/>
      <text x="32" y="38" textAnchor="middle" fill="#FF6B1A" fontSize="22" fontFamily="serif" fontWeight="bold">{'\u0950'}</text>
      <rect x="16" y="46" width="32" height="3" rx="1" fill="#FF6B1A" opacity="0.4"/>
      <line x1="20" y1="16" x2="20" y2="20" stroke="#f5c518" strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="44" y1="16" x2="44" y2="20" stroke="#f5c518" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

function MathIcon({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <circle cx="32" cy="32" r="26" fill="#2d1b69" stroke="#9b59b6" strokeWidth="1.5"/>
      <text x="32" y="28" textAnchor="middle" fill="#f5c518" fontSize="16" fontFamily="monospace" fontWeight="bold">16</text>
      <text x="32" y="42" textAnchor="middle" fill="#2ecc71" fontSize="10" fontFamily="sans-serif">SUTRAS</text>
      <line x1="18" y1="32" x2="46" y2="32" stroke="#9b59b6" strokeWidth="0.8" opacity="0.4"/>
    </svg>
  );
}

function MatchIcon({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <rect x="6" y="10" width="22" height="28" rx="4" fill="#E84393" opacity="0.2" stroke="#E84393" strokeWidth="1.5"/>
      <rect x="36" y="10" width="22" height="28" rx="4" fill="#E84393" opacity="0.2" stroke="#E84393" strokeWidth="1.5"/>
      <text x="17" y="29" textAnchor="middle" fill="#E84393" fontSize="14">{'\uD83E\uDD85'}</text>
      <text x="47" y="29" textAnchor="middle" fill="#E84393" fontSize="14">{'\uD83D\uDC99'}</text>
      <path d="M28 24 L36 24" stroke="#f5c518" strokeWidth="2" strokeDasharray="2 2"/>
      <rect x="16" y="44" width="32" height="8" rx="3" fill="#E84393" opacity="0.15"/>
      <text x="32" y="50" textAnchor="middle" fill="#E84393" fontSize="6" fontFamily="sans-serif">MATCH</text>
    </svg>
  );
}

function PlanetIcon({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <circle cx="32" cy="32" r="28" fill="#0a1628"/>
      <circle cx="22" cy="22" r="6" fill="#f5c518" opacity="0.8"/>
      <circle cx="42" cy="18" r="3" fill="#c0c0c0" opacity="0.6"/>
      <circle cx="38" cy="38" r="5" fill="#e74c3c" opacity="0.6"/>
      <circle cx="18" cy="42" r="4" fill="#3498db" opacity="0.5"/>
      <circle cx="48" cy="30" r="3.5" fill="#f1c40f" opacity="0.5"/>
      <ellipse cx="38" cy="38" rx="8" ry="2" fill="none" stroke="#e74c3c" strokeWidth="0.8" opacity="0.4"/>
      <polygon points="32,50 29,56 35,56" fill="#f5c518" opacity="0.7"/>
    </svg>
  );
}

function GardenIcon({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <rect x="8" y="44" width="48" height="12" rx="2" fill="#5D4037"/>
      <ellipse cx="20" cy="44" rx="8" ry="12" fill="#2ecc71"/>
      <ellipse cx="32" cy="42" rx="6" ry="14" fill="#27ae60"/>
      <ellipse cx="44" cy="44" rx="8" ry="12" fill="#2ecc71"/>
      <circle cx="20" cy="36" r="3" fill="#f1c40f"/>
      <circle cx="44" cy="36" r="3" fill="#e74c3c"/>
      <circle cx="32" cy="32" r="2.5" fill="#9b59b6"/>
      <line x1="20" y1="56" x2="20" y2="44" stroke="#8B6914" strokeWidth="2"/>
      <line x1="32" y1="56" x2="32" y2="42" stroke="#8B6914" strokeWidth="2"/>
      <line x1="44" y1="56" x2="44" y2="44" stroke="#8B6914" strokeWidth="2"/>
    </svg>
  );
}

function GitaIcon({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <rect x="14" y="6" width="36" height="52" rx="3" fill="#2a1810" stroke="#d4a12a" strokeWidth="1.5"/>
      <rect x="18" y="10" width="28" height="44" rx="2" fill="#FFF8F0"/>
      <line x1="22" y1="18" x2="42" y2="18" stroke="#d4a12a" strokeWidth="1" opacity="0.4"/>
      <line x1="22" y1="24" x2="42" y2="24" stroke="#d4a12a" strokeWidth="1" opacity="0.4"/>
      <line x1="22" y1="30" x2="42" y2="30" stroke="#d4a12a" strokeWidth="1" opacity="0.4"/>
      <line x1="22" y1="36" x2="38" y2="36" stroke="#d4a12a" strokeWidth="1" opacity="0.4"/>
      <text x="32" y="48" textAnchor="middle" fill="#d4a12a" fontSize="8" fontFamily="serif" fontWeight="bold">GITA</text>
      <circle cx="32" cy="14" r="2" fill="#d4a12a"/>
    </svg>
  );
}

function QuizIcon({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <rect x="10" y="12" width="44" height="36" rx="6" fill="#2a1810" stroke="#f5c518" strokeWidth="2"/>
      <path d="M22 48 L20 58 L32 48" fill="#2a1810" stroke="#f5c518" strokeWidth="2" strokeLinejoin="round"/>
      <path d="M26 24 Q26 18 32 18 Q38 18 38 23 Q38 27 32 29 L32 33" stroke="#ff9933" strokeWidth="3.5" fill="none" strokeLinecap="round"/>
      <circle cx="32" cy="40" r="2.5" fill="#ff9933"/>
    </svg>
  );
}

function SleepIcon({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <ellipse cx="30" cy="44" rx="22" ry="10" fill="#8b5a34"/>
      <circle cx="46" cy="36" r="10" fill="#8b5a34"/>
      <path d="M38 28 L41 21 L44 27 L47 20 L50 27 L54 22 L54 30 Z" fill="#d4a843"/>
      <path d="M42 36 Q44 38 46 36" stroke="#1a0a00" strokeWidth="1.5" fill="none"/>
      <path d="M48 36 Q50 38 52 36" stroke="#1a0a00" strokeWidth="1.5" fill="none"/>
      <rect x="6" y="52" width="52" height="5" rx="1" fill="#d4a843"/>
      <text x="14" y="22" fill="#f5e6c8" fontSize="12" fontFamily="Georgia" fontWeight="bold">z</text>
      <text x="22" y="14" fill="#f5e6c8" fontSize="9" fontFamily="Georgia" fontWeight="bold" opacity="0.7">z</text>
    </svg>
  );
}

function DemonessIcon({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <circle cx="40" cy="18" r="9" fill="#5e3448"/>
      <path d="M28 14 L32 4 L36 12 L40 2 L44 12 L48 4 L52 14" stroke="#140b08" strokeWidth="3" fill="none"/>
      <circle cx="37" cy="17" r="1.8" fill="#ff3a20"/><circle cx="43" cy="17" r="1.8" fill="#ff3a20"/>
      <ellipse cx="40" cy="40" rx="11" ry="16" fill="#4b2a3a"/>
      <path d="M8 40 C4 30, 4 22, 10 12" stroke="#f5c518" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
      <line x1="10" y1="12" x2="8" y2="40" stroke="#e8d5b7" strokeWidth="1"/>
      <line x1="9" y1="26" x2="30" y2="30" stroke="#e8d5b7" strokeWidth="2" strokeLinecap="round"/>
      <polygon points="32,30 27,27 27,33" fill="#ddd"/>
    </svg>
  );
}

function ChoiceIcon({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <path d="M32 58 L32 30" stroke="#d4a843" strokeWidth="3" strokeLinecap="round"/>
      <path d="M32 30 L14 12" stroke="#8E1B1B" strokeWidth="4" strokeLinecap="round"/>
      <path d="M32 30 L50 12" stroke="#1A5276" strokeWidth="4" strokeLinecap="round"/>
      <polygon points="10,8 20,10 12,18" fill="#8E1B1B"/>
      <polygon points="54,8 44,10 52,18" fill="#1A5276"/>
      <circle cx="32" cy="30" r="5" fill="#d4a843"/>
      <circle cx="32" cy="58" r="3" fill="#1E8449"/>
    </svg>
  );
}

function CharacterIcon({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <circle cx="32" cy="32" r="26" fill="#1a0a2e" stroke="#c9a648" strokeWidth="1.5"/>
      <circle cx="32" cy="26" r="9" fill="#d4884a"/>
      <polygon points="24,19 27,10 30,16 32,8 34,16 37,10 40,19" fill="#f5c518"/>
      <path d="M17 50 Q32 34 47 50" fill="#ff9933"/>
      <circle cx="32" cy="32" r="30" fill="none" stroke="#f5c518" strokeWidth="1" opacity="0.35"/>
    </svg>
  );
}

function BrokenBowIcon({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <path d="M8 44 Q16 18 29 14" stroke="#2a3a7e" strokeWidth="5" fill="none" strokeLinecap="round"/>
      <path d="M35 14 Q48 18 56 44" stroke="#2a3a7e" strokeWidth="5" fill="none" strokeLinecap="round"/>
      <path d="M32 6 L30 14 L34 18 L31 26" stroke="#fff3b0" strokeWidth="2" fill="none" strokeLinecap="round"/>
      <circle cx="32" cy="16" r="10" fill="#9fc0ff" opacity="0.25"/>
      <line x1="8" y1="44" x2="56" y2="44" stroke="#f0e6d0" strokeWidth="1" opacity="0.6"/>
      <rect x="10" y="50" width="44" height="6" rx="2" fill="#3c3f48"/>
    </svg>
  );
}

function RekhaIcon({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <path d="M14 44 L32 30 L50 44 Z" fill="#8a6a2a"/>
      <rect x="18" y="44" width="28" height="14" fill="#5a4020"/>
      <rect x="29" y="48" width="6" height="10" fill="#2a1a0a"/>
      <path d="M4 26 Q32 34 60 26" stroke="#ffd24a" strokeWidth="3" fill="none"/>
      <path d="M26 6 L38 6 L36 20 L28 20 Z" fill="#d0602a"/>
      <circle cx="32" cy="4" r="3" fill="#c99a6a"/>
    </svg>
  );
}

function TwoDeerIcon({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <ellipse cx="18" cy="40" rx="11" ry="7" fill="#9a6a3a"/>
      <circle cx="27" cy="30" r="4" fill="#9a6a3a"/>
      <path d="M26 26 L23 18 M28 26 L31 18" stroke="#5a3a1a" strokeWidth="1.5"/>
      <ellipse cx="44" cy="40" rx="11" ry="7" fill="#e8b830"/>
      <circle cx="53" cy="30" r="4" fill="#e8b830"/>
      <path d="M52 26 L49 18 M54 26 L57 18" stroke="#c9a648" strokeWidth="1.5"/>
      <circle cx="49" cy="18" r="1.8" fill="#6ad0ff"/><circle cx="57" cy="18" r="1.8" fill="#6ad0ff"/>
      <circle cx="40" cy="39" r="1.4" fill="#f4f4ff"/><circle cx="46" cy="42" r="1.4" fill="#f4f4ff"/>
      <circle cx="44" cy="38" r="16" stroke="#FFD700" strokeWidth="1" opacity="0.35"/>
    </svg>
  );
}

function DamaruIcon({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <circle cx="32" cy="32" r="28" stroke="#ff8c2a" strokeWidth="2" strokeDasharray="4 3" opacity="0.7"/>
      <path d="M18 16 L46 16 L34 32 L46 48 L18 48 L30 32 Z" fill="#c9a648"/>
      <ellipse cx="32" cy="16" rx="14" ry="3" fill="#f5e6c8"/>
      <ellipse cx="32" cy="48" rx="14" ry="3" fill="#f5e6c8"/>
      <path d="M30 32 Q20 30 14 38" stroke="#cc2936" strokeWidth="1.5" fill="none"/>
      <circle cx="13" cy="39" r="2.5" fill="#cc2936"/>
      <path d="M34 32 Q44 34 50 26" stroke="#cc2936" strokeWidth="1.5" fill="none"/>
      <circle cx="51" cy="25" r="2.5" fill="#cc2936"/>
    </svg>
  );
}

const GAME_ICONS = {
  'rama-archery': ArcheryIcon,
  'lanka-battle': TowerIcon,
  'lanka-dash': RunnerIcon,
  'golden-deer': DeerIcon,
  'chakravyuha': MazeIcon,
  'samudra-manthan': ChurnIcon,
  'dashavatara': AvatarIcon,
  'shakuni-dice': DiceIcon,
  'astra': CardIcon,
  'makhan-chor': ButterIcon,
  'mantra-mayhem': MantraIcon,
  'sutra-speed': MathIcon,
  'deva-match': MatchIcon,
  'navagraha-navigator': PlanetIcon,
  'rishis-garden': GardenIcon,
  'gita-wisdom-tower': GitaIcon,
  'sandhi-smasher': MantraIcon,
  'dhammapada-daily': GitaIcon,
  'vahana-verse': MatchIcon,
  'dharma-court': DiceIcon,
  'wheel-of-samsara': MazeIcon,
  'palm-reading': MantraIcon,
  'kurukshetra': MazeIcon,
  'kurukshetra-lines': ArcheryIcon,
  'kurukshetra-fpv': ArcheryIcon,
  'kuru-of-duty': ArcheryIcon,
  'hanumans-leap': RunnerIcon,
  'ramayana-movie-quiz': QuizIcon,
  'which-ramayana-character': CharacterIcon,
  'tataka-takedown': DemonessIcon,
  'string-shivas-bow': BrokenBowIcon,
  'lakshman-rekha': RekhaIcon,
  'spot-the-real-deer': TwoDeerIcon,
  'kumbhakarna-wake-up': SleepIcon,
  'vibhishanas-choice': ChoiceIcon,
  'shiva-tandava': DamaruIcon,
};

const GAME_COMPONENTS = {
  'rama-archery': RamaArchery,
  'lanka-battle': LankaBattle,
  'lanka-dash': LankaDash,
  'golden-deer': GoldenDeerChase,
  'chakravyuha': Chakravyuha,
  'samudra-manthan': SamudraManthan,
  'dashavatara': Dashavatara,
  'shakuni-dice': ShakuniDice,
  'astra': Astra,
  'makhan-chor': MakhanChor,
  'mantra-mayhem': MantraMayhem,
  'sutra-speed': SutraSpeed,
  'deva-match': DevaMatch,
  'navagraha-navigator': NavagrahaNavigator,
  'rishis-garden': RishisGarden,
  'gita-wisdom-tower': GitaWisdomTower,
  'sandhi-smasher': SandhiSmasher,
  'dhammapada-daily': DhammapadaDaily,
  'vahana-verse': VahanaVerse,
  'dharma-court': DharmaCourt,
  'wheel-of-samsara': WheelOfSamsara,
  'palm-reading': PalmReading,
  'kurukshetra': Kurukshetra,
  'kurukshetra-lines': KurukshetraLines,
  'hanumans-leap': HanumansLeap,
  'kurukshetra-fpv': KurukshetraFPV,
  'kuru-of-duty': KurukshetraDuty,
  'kumbhakarna-wake-up': KumbhakarnaWakeUp,
  'vibhishanas-choice': VibhishanaChoice,
  'shiva-tandava': ShivaTandava,
  'tataka-takedown': TatakaTakedown,
  'string-shivas-bow': StringShivasBow,
  'lakshman-rekha': LakshmanRekha,
  'spot-the-real-deer': SpotTheRealDeer,
  'ramayana-movie-quiz': RamayanaMovieQuiz,
  'which-ramayana-character': WhichRamayanaCharacter,
};

// Games with shareable result pages at /games/<id>/<result>. Each result has
// its own prerendered OG card (scripts/prerender.js); any other sub-path 404s.
const GAME_RESULT_IDS = {
  'which-ramayana-character': Object.keys(RAMAYANA_CHARACTERS.results),
};

// Display order, ranked by measured performance (GA4, 30d to 2026-09-03).
//
// Ordered on the metrics that are trustworthy at the page level — sessions,
// search entrances and dwell — NOT on replay rate. Only 8 of 27 game
// components could report a replay when this was written, so replay counts
// measured instrumentation coverage rather than player behaviour. App.js now
// emits a universal game_replay; revisit this order once that has a month of
// clean data behind it.
//
//   kuru-of-duty      103 sessions, 38 search entrances, 200s dwell, 183 scores
//   kurukshetra        31 sessions, 109s dwell, highest in-game replay signal
//   hanumans-leap      26 sessions, 159s dwell
//   kurukshetra-lines  22 sessions, 239s dwell
//   dashavatara        14 sessions, 368s dwell — the longest on the site
//
// The long tail stays listed on purpose. 143 of 196 sessions start at "/" and
// visitors view 12 pages per session, so the catalogue IS the discovery path
// to the games that convert. Hiding it would remove the route to the winner.
const games = [
  { id: 'kuru-of-duty', ogImage: '/og-image.png', title: 'Kuru of Duty', subtitle: 'Hold the Centre', description: "A full first-person war on the plain of Kurukshetra. Up to 40,000 soldiers fight around you in real time while you hunt nine named Kaurava commanders — Bhishma, Drona, Karna, Shalya, Dushasana, Shakuni, Jayadratha, Ashwatthama, Duryodhana. Charge your bow for range, lead moving targets, earn valor, and loose the Agneyastra when the line breaks.", color: '#d9a441', source: 'Mahabharata', genre: '3D First-Person Shooter', difficulty: 'Hard', tone: 'Epic', isNew: true, controls: 'WASD move, Shift sprint, Space jump, Ctrl crouch, hold click to draw / release to fire, E Agneyastra' },
  { id: 'kurukshetra', ogImage: '/og-image.png', title: 'Kurukshetra', subtitle: 'The Great War', description: 'Command Arjuna, Bhima, Karna, or Duryodhana in a 3D battlefield with 15 allies fighting alongside you. Clear a 7-wave Battle Day, break commander waves, level up weapons from Basic to Mythic, and unleash devastating astras.', color: '#ba7517', source: 'Mahabharata', genre: '3D Action RPG', difficulty: 'Hard', tone: 'Epic', isNew: true, controls: 'WASD move, Click attack, Right-click/E astra, Q potion' },
  { id: 'hanumans-leap', ogImage: '/og-image.png', title: "Hanuman's Leap", subtitle: 'Parkour to Lanka', description: 'A 3D Roblox-style obby. Play Hanuman leaping across 100 floating stones over the ocean to reach Lanka. Double-jump, wind-burst across gaps, dodge spike traps, collect Rama\'s rings, hit checkpoints.', color: '#ff9933', source: 'Ramayana', genre: '3D Parkour / Obby', difficulty: 'Medium', tone: 'Playful', isNew: true, controls: 'WASD move, Space jump, Shift/F wind burst, R respawn' },
  { id: 'ramayana-movie-quiz', ogImage: '/og/ramayana-movie-quiz.png', title: 'Ramayana Movie Quiz', subtitle: 'How Well Do You Know Part 1?', description: "Ten quick questions on the story of Ramayana Part 1, from Tataka and the bow of Shiva to the golden deer and Jatayu. Every answer comes from Valmiki's Ramayana, with a short explanation after each one. Get your score and challenge a friend.", color: '#f5c518', source: 'Ramayana', genre: 'Trivia Quiz', difficulty: 'Easy', tone: 'Playful', isNew: true, controls: 'Tap an answer, then Next' },
  { id: 'which-ramayana-character', ogImage: '/og/which-ramayana-character.png', title: 'Which Ramayana Character Are You?', subtitle: 'Personality Quiz', description: 'Eight quick questions match you with Rama, Sita, Lakshmana, Hanuman, Ravana, Bharata, Kaikeyi or Jatayu. Learn what each one did in the Ramayana, then play their game and share your result.', color: '#ff9933', source: 'Ramayana', genre: 'Personality Quiz', difficulty: 'Easy', tone: 'Playful', isNew: true, controls: 'Tap an answer' },
  { id: 'kurukshetra-lines', title: 'Kurukshetra: Lines of War', subtitle: 'Hold the Pandava Line', description: 'Ride with Arjuna through a living red-vs-blue battlefield. Your armies march automatically while every hero kill upgrades the next wave. Break 16 named Kaurava commanders, unleash Agneyastra, and bring down the eastern gate.', color: '#f0bf4f', source: 'Mahabharata', genre: 'Auto-Battle Action', difficulty: 'Medium', tone: 'Epic', isNew: true, controls: 'WASD/drag move, tap enemies to focus, E/Space for Agneyastra', ogImageWidth: 1280, ogImageHeight: 820 },
  { id: 'dashavatara', title: 'Dashavatara', subtitle: 'Ten Avatars Arcade Run', description: "Launch straight into ten shifting arcade challenges as Vishnu's avatars, from Matsya's flood rescue to Kalki's apocalyptic sprint, with each form changing the controls and objective.", color: '#3f51b5', source: 'Puranas / Vedic', genre: 'Platformer', difficulty: 'Medium', tone: 'Epic', isNew: true, controls: 'Arrow keys move, Space/ACT action, Z special' },
  { id: 'kurukshetra-fpv', ogImage: '/og-image.png', title: 'Kurukshetra FPS', subtitle: 'First-Person Battlefield Run', description: "A browser-native 3D first-person run across Kurukshetra. Sprint through moving troops, dodge arrow volleys, fire your bow, lead moving targets, and clear the field before your composure breaks.", color: '#c9a648', source: 'Mahabharata', genre: '3D First-Person Action', difficulty: 'Medium', tone: 'Epic', isNew: true, controls: 'WASD move, Shift sprint, Mouse look, Click/F fire' },
  { id: 'chakravyuha', title: 'Chakravyuha', subtitle: 'Labyrinth of No Return', description: "Dash through 7 rotating rings as Abhimanyu, slash guardian bosses, dodge orbiting sentinels, and push deeper across brutal runs.", color: '#c9a648', source: 'Mahabharata', genre: 'Roguelike Maze', difficulty: 'Hard', tone: 'Epic', isNew: true, controls: 'WASD move, Space slash, Shift/F dash' },
  // Ramayana Part 1 scene games (2026-11-02), one per film scene.
  { id: 'tataka-takedown', ogImage: '/og/tataka-takedown.png', title: 'Tataka Takedown', subtitle: "Rama's First Battle", description: "Young Rama's first fight from the Ramayana. In Vishvamitra's forest, draw the bow against the demoness Tataka, shoot her stones out of the air, and win before dusk.", color: '#e0703a', source: 'Ramayana', genre: 'Archery Boss Fight', difficulty: 'Easy', tone: 'Epic', isNew: true, controls: 'Hold to draw, aim with mouse or finger, release to shoot. Keyboard: Up/Down aim, hold Space' },
  { id: 'string-shivas-bow', ogImage: '/og/string-shivas-bow.png', title: "String Shiva's Bow", subtitle: "Sita's Swayamvara", description: "No king could even lift it. As Rama, lift, string and draw the great bow of Shiva in King Janaka's court until it breaks with a sound like thunder.", color: '#9fc0ff', source: 'Ramayana', genre: 'One-Tap Timing', difficulty: 'Easy', tone: 'Epic', isNew: true, controls: 'Hold and release in the gold zone (tap, click or Space)' },
  { id: 'lakshman-rekha', ogImage: '/og/lakshman-rekha.png', title: 'Lakshman Rekha', subtitle: "Guard Sita's Hut", description: "Ravana's rakshasas take any form they wish. Spot the false ascetic, the golden deer, the horned deer and the false cry before they cross the line, and let real guests pass.", color: '#ffd24a', source: 'Ramayana', genre: 'Spot-and-Stop Defense', difficulty: 'Medium', tone: 'Strategic', isNew: true, controls: 'Tap the tricks. Keyboard: 1/2/3 or Left/Down/Right per path' },
  { id: 'spot-the-real-deer', ogImage: '/og/spot-the-real-deer.png', title: 'Find the False Deer', subtitle: "Maricha's Illusion", description: "Maricha hides among the real deer of Panchavati. Ten rounds of odd-one-out, each with a quieter tell taken from Valmiki's description of the golden deer.", color: '#e8b830', source: 'Ramayana', genre: 'Odd-One-Out Puzzle', difficulty: 'Easy', tone: 'Playful', isNew: true, controls: 'Tap the false deer. Keyboard: arrows to choose, Space to pick' },
  { id: 'kumbhakarna-wake-up', ogImage: '/og/kumbhakarna-wake-up.png', title: 'Kumbhakarna Wake-Up', subtitle: 'Wake the Sleeping Giant', description: "Ravana needs his giant brother for the war, but Kumbhakarna is asleep. Beat drums, blow conches, march elephants and lay out a feast, as the rakshasas do in Valmiki's Yuddha Kanda. Time your noise to his breathing and wake him as fast as you can.", color: '#e67e22', source: 'Ramayana', genre: 'Comic Tapping', difficulty: 'Easy', tone: 'Playful', isNew: true, controls: 'Tap the tool buttons, or D drums, C conch, E elephants, F feast (1-4)' },
  { id: 'vibhishanas-choice', ogImage: '/og/vibhishanas-choice.png', title: "Vibhishana's Choice", subtitle: 'Stay with Ravana or Join Rama', description: "Six moral choices from Valmiki's Yuddha Kanda. Speak in Ravana's war council, answer his insult, ask Rama for refuge, see through Indrajit's illusion, and decide on your brother's last rites. See what Vibhishana really did after each choice.", color: '#1E8449', source: 'Ramayana', genre: 'Moral Choice', difficulty: 'Easy', tone: 'Strategic', isNew: true, controls: 'Tap or click a choice, or press 1-4; Enter to continue' },
  { id: 'shiva-tandava', ogImage: '/og/shiva-tandava.png', title: 'Shiva Tandava', subtitle: "Rhythm of Ravana's Hymn", description: "Tap the first verse of the Shiva Tandava Stotram, the hymn tradition credits to Ravana, syllable by syllable. Short and long syllables fall on two lanes in its pancha-chamara metre, over an original synthesized damaru beat.", color: '#ff8c2a', source: 'Ramayana', genre: 'Rhythm', difficulty: 'Medium', tone: 'Epic', isNew: true, controls: 'Tap Short / Long, or F / J (Left / Right arrows)' },
  { id: 'lanka-dash', title: 'Lanka Dash', subtitle: "Hanuman's Burning Run", description: "Leap across Lanka's rooftops as Hanuman with his tail ablaze! An endless runner where every building you touch bursts into flames.", color: '#FF6B35', source: 'Ramayana', genre: 'Endless Runner', difficulty: 'Easy', tone: 'Playful', isNew: false, controls: 'Hold to leap, Shift/F or right-side tap to dash' },
  { id: 'rama-archery', title: 'Rama Archery Challenge', subtitle: 'Test Your Divine Aim', description: 'Channel the spirit of Lord Rama and prove your mastery with the bow. Hit divine targets for bonus points!', color: '#f5c518', source: 'Ramayana', genre: 'Shooting', difficulty: 'Easy', tone: 'Playful', isNew: false, controls: 'Mouse to aim, Click to shoot' },
  { id: 'samudra-manthan', title: 'Samudra Manthan', subtitle: 'Churn the Cosmic Ocean', description: "Churn the ocean of milk with edge-timed rhythm pulls, react to Halahala poison, and survive a clearer Amrit boss phase with arrow blocks and Rahu choices.", color: '#0a8fd6', source: 'Puranas', genre: 'Rhythm', difficulty: 'Medium', tone: 'Epic', isNew: true, controls: 'A/D or Left/Right on beat edges, S/Down for poison, arrows for boss' },
  { id: 'astra', title: 'Astra', subtitle: 'Divine Weapons Card Battler', description: "Launch straight into tactical card combat across 18 days of Kurukshetra. Play elemental astras, read enemy intent, win battles, draft rewards, and build a stronger run.", color: '#9b59b6', source: 'Mahabharata', genre: 'Deck-Builder', difficulty: 'Hard', tone: 'Strategic', isNew: true, controls: 'Click cards to attack, End Turn to defend' },
  { id: 'makhan-chor', title: 'Makhan Chor', subtitle: "Krishna's Butter Heist", description: "Sneak through Vrindavan as young Krishna, steal butter pots, hide in hay, use flute stuns, and command Gopa friends while patrols now move on readable stealth routes.", color: '#ffc107', source: 'Krishna Leela', genre: 'Stealth Puzzle', difficulty: 'Easy', tone: 'Playful', isNew: true, controls: 'WASD/Arrows move, H hide, Space flute, E Gopa' },
  { id: 'shakuni-dice', title: "Shakuni's Dice", subtitle: 'Loaded Dice Push-Your-Luck', description: "Step straight into the court, wager escalating stakes, read suspicious roll patterns, and challenge Shakuni when the loaded dice give him away.", color: '#d4a12a', source: 'Mahabharata', genre: 'Push-Your-Luck', difficulty: 'Medium', tone: 'Strategic', isNew: true, controls: 'Click wager, roll, challenge, or walk away' },
  { id: 'lanka-battle', title: 'Lanka Battle', subtitle: 'Tower Defense', description: "Place vanara warriors strategically to defend against Ravana's demon army. Survive 10 waves and defeat Ravana!", color: '#FF4500', source: 'Ramayana', genre: 'Strategy', difficulty: 'Medium', tone: 'Strategic', isNew: false, controls: 'Click to place towers, right-click to upgrade' },
  { id: 'golden-deer', title: 'Golden Deer Chase', subtitle: 'Catch the Illusion', description: "Chase the enchanted golden deer through the forest. But beware \u2014 not everything that glitters is what it seems...", color: '#7B68EE', source: 'Ramayana', genre: 'Chase', difficulty: 'Easy', tone: 'Playful', isNew: false, controls: 'Arrow keys or tap to chase' },
  { id: 'mantra-mayhem', title: 'Mantra Mayhem', subtitle: 'Sacred Sanskrit Defense', description: "Type Sanskrit mantras to power divine shields against approaching demons. Chain 200+ words through frantic typing gameplay.", color: '#FF6B1A', source: 'Sanskrit', genre: 'Typing Defense', difficulty: 'Easy', tone: 'Epic', isNew: true, controls: 'Type the displayed words to cast spells' },
  { id: 'sutra-speed', title: 'Sutra Speed', subtitle: 'Vedic Math Mastery', description: "Race through the 16 Vedic Math sutras with timed calculation challenges and speed-run scoring.", color: '#6C5CE7', source: 'Vedic Math', genre: 'Speed Math', difficulty: 'Medium', tone: 'Strategic', isNew: true, controls: 'Type answers, Enter to submit' },
  { id: 'deva-match', title: 'Deva Match', subtitle: 'Divine Memory Puzzle', description: "Match Hindu deities to their vahanas, weapons, consorts, and gemstones. 100+ deity-attribute pairs to discover.", color: '#E84393', source: 'Hindu Pantheon', genre: 'Memory Match', difficulty: 'Easy', tone: 'Playful', isNew: true, controls: 'Click cards to flip and match' },
  { id: 'navagraha-navigator', title: 'Navagraha Navigator', subtitle: 'Vedic Space Odyssey', description: "Pilot a celestial vimana through the Vedic cosmos, visiting 9 planetary deities. Collect all 9 Navaratna gemstones.", color: '#0984E3', source: 'Vedic Cosmology', genre: 'Space Exploration', difficulty: 'Medium', tone: 'Epic', isNew: true, controls: 'Arrow keys to navigate, Space to interact' },
  { id: 'rishis-garden', title: "Rishi's Garden", subtitle: 'Ayurvedic Herb Wisdom', description: "Grow an ashram garden of 15+ real Ayurvedic herbs. Prescribe herbal combinations for villagers' ailments.", color: '#00B894', source: 'Ayurveda', genre: 'Idle Farming', difficulty: 'Easy', tone: 'Playful', isNew: true, controls: 'Click to plant, harvest, and prescribe' },
  { id: 'gita-wisdom-tower', title: 'Gita Wisdom Tower', subtitle: 'Ascend 18 Floors of Philosophy', description: "Solve philosophy puzzles by completing Bhagavad Gita verses across all 18 chapters.", color: '#D4A843', source: 'Bhagavad Gita', genre: 'Word Puzzle', difficulty: 'Medium', tone: 'Strategic', isNew: true, controls: 'Click or type to fill in blanks' },
  { id: 'sandhi-smasher', title: 'Sandhi Smasher', subtitle: 'Slice Sanskrit Compounds', description: "Fruit Ninja meets Sanskrit grammar. Slice compound words at the exact junction point across 60+ arcade challenges.", color: '#FF6B00', source: 'Sanskrit', genre: 'Word Arcade', difficulty: 'Easy', tone: 'Playful', isNew: true, controls: 'Click/swipe to slice words' },
  { id: 'dhammapada-daily', title: 'Dhammapada Daily', subtitle: 'Daily Wisdom Puzzle', description: "One Dhammapada verse per day with scrambled key words. 30 verses, streak tracking, shareable results.", color: '#00B894', source: 'Buddhism', genre: 'Daily Word', difficulty: 'Easy', tone: 'Playful', isNew: true, controls: 'Click/drag to unscramble words' },
  { id: 'vahana-verse', title: 'Vahana Verse', subtitle: 'Match the Divine Mounts', description: "Match deities to their correct vahana before time runs out. 30 deity-mount pairs across 6 fast rounds.", color: '#D4A843', source: 'Hindu Pantheon', genre: 'Timed Match', difficulty: 'Easy', tone: 'Playful', isNew: true, controls: 'Click deity then click matching mount' },
  { id: 'dharma-court', title: 'Dharma Court', subtitle: 'Judge the Mahabharata', description: "15 moral dilemmas from the Mahabharata. Hear testimony from Bhishma, Draupadi, and Krishna. Deliver your judgment.", color: '#6B0F2E', source: 'Mahabharata', genre: 'Moral Choice', difficulty: 'Medium', tone: 'Strategic', isNew: true, controls: 'Click to choose your judgment' },
  { id: 'wheel-of-samsara', title: 'Wheel of Samsara', subtitle: 'Escape the Cycle of Rebirth', description: "A roguelike through Buddhist cosmology. Make moral choices across lives, cycle through 6 realms.", color: '#5B2C6F', source: 'Buddhism', genre: 'Roguelike', difficulty: 'Medium', tone: 'Epic', isNew: true, controls: 'Click to make choices, Arrow keys in action phases' },
  { id: 'palm-reading', ogImage: '/og-image.png', title: 'Cosmic Palm Reading', subtitle: 'Hilarious Fortune Teller', description: "Place your palm on the screen for a mystical reading. 150+ funny predictions. Personalized, shareable, and absurdly accurate.", color: '#9b59b6', source: 'Jyotish', genre: 'Comedy / Fortune', difficulty: 'Easy', tone: 'Playful', isNew: true, controls: 'Place palm on screen, tap to reveal' },
];

// ─── Filter System ─────────────────────────────────────────────────
const SOURCE_TAG_COLORS = {
  'Ramayana': { bg: 'rgba(245,197,24,0.15)', text: '#f5c518' },
  'Mahabharata': { bg: 'rgba(201,166,72,0.15)', text: '#c9a648' },
  'Puranas': { bg: 'rgba(63,81,181,0.15)', text: '#7986cb' },
  'Puranas / Vedic': { bg: 'rgba(63,81,181,0.15)', text: '#7986cb' },
  'Krishna Leela': { bg: 'rgba(255,193,7,0.15)', text: '#ffc107' },
  'Sanskrit': { bg: 'rgba(255,107,26,0.15)', text: '#FF6B1A' },
  'Vedic Math': { bg: 'rgba(108,92,231,0.15)', text: '#6C5CE7' },
  'Hindu Pantheon': { bg: 'rgba(232,67,147,0.15)', text: '#E84393' },
  'Vedic Cosmology': { bg: 'rgba(9,132,227,0.15)', text: '#0984E3' },
  'Ayurveda': { bg: 'rgba(0,184,148,0.15)', text: '#00B894' },
  'Bhagavad Gita': { bg: 'rgba(212,168,67,0.15)', text: '#D4A843' },
  'Buddhism': { bg: 'rgba(0,184,148,0.15)', text: '#00B894' },
  'Jyotish': { bg: 'rgba(155,89,182,0.15)', text: '#9b59b6' },
};

const DIFFICULTY_TAG_COLORS = {
  'Easy': { bg: 'rgba(46,204,113,0.15)', text: '#2ecc71' },
  'Medium': { bg: 'rgba(241,196,15,0.15)', text: '#f1c40f' },
  'Hard': { bg: 'rgba(231,76,60,0.15)', text: '#e74c3c' },
};

function filterGames(gameList, filters, searchQuery) {
  return gameList.filter(game => {
    if (filters.source !== 'All' && !game.source.startsWith(filters.source)) return false;
    if (filters.difficulty !== 'All' && game.difficulty !== filters.difficulty) return false;
    if (filters.tone !== 'All' && game.tone !== filters.tone) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const haystack = `${game.title} ${game.subtitle} ${game.description} ${game.source} ${game.genre}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });
}

function HomePolishStyles() {
  return (
    <style>{`
      html {
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
      }

      .home-shell {
        position: relative;
        overflow: hidden;
        isolation: isolate;
      }

      .home-shell::before {
        content: "";
        position: fixed;
        inset: 0;
        z-index: -1;
        pointer-events: none;
        background:
          linear-gradient(90deg, rgba(245, 197, 24, 0.035) 1px, transparent 1px),
          linear-gradient(180deg, rgba(255, 255, 255, 0.035) 1px, transparent 1px);
        background-size: 72px 72px;
        mask-image: linear-gradient(180deg, rgba(0,0,0,0.9), rgba(0,0,0,0.18));
      }

      .home-shell h1,
      .home-shell h2,
      .home-shell h3 {
        text-wrap: balance;
      }

      .home-shell p,
      .home-shell li,
      .home-shell button {
        text-wrap: pretty;
      }

      .home-shell button {
        appearance: none;
        -webkit-appearance: none;
      }

      .home-hero {
        width: min(920px, 100%);
        display: block;
        margin: 0 auto 42px;
      }

      .home-hero-copy {
        display: grid;
        gap: 18px;
        justify-items: center;
        text-align: center;
        padding: clamp(18px, 5vw, 54px) 0 clamp(10px, 2vw, 24px);
      }

      .home-eyebrow {
        width: fit-content;
        color: #0b0d14;
        background: #f5c518;
        border-radius: 999px;
        padding: 8px 12px;
        font-family: ${FONT.body};
        font-size: 10px;
        font-weight: 900;
        letter-spacing: 2px;
        text-transform: uppercase;
        box-shadow:
          0 0 0 1px rgba(255, 255, 255, 0.08),
          0 10px 28px rgba(245, 197, 24, 0.18);
      }

      .home-hero-title {
        color: #fff3bf;
        font-size: clamp(44px, 7vw, 82px);
        line-height: 0.92;
        margin: 0;
        letter-spacing: 0;
        text-shadow: 0 18px 60px rgba(0, 0, 0, 0.45);
      }

      .home-hero-lede {
        max-width: 650px;
        color: #cbd6ff;
        font-family: ${FONT.body};
        font-size: clamp(15px, 1.55vw, 18px);
        line-height: 1.65;
        margin: 0;
      }

      .home-actions {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 10px;
      }

      .home-action {
        min-height: 44px;
        border: 0;
        border-radius: 10px;
        padding: 0 18px;
        cursor: pointer;
        font-family: ${FONT.body};
        font-size: 12px;
        font-weight: 900;
        letter-spacing: 1.6px;
        text-transform: uppercase;
        transition-property: scale, transform, box-shadow, background-color, color;
        transition-duration: 160ms;
        transition-timing-function: ease-out;
      }

      .home-action:active,
      .game-card-button:active,
      .filter-pill:active {
        scale: 0.96;
      }

      .home-hero-kicker {
        margin: 0 0 6px;
        font-size: 0.78rem;
        letter-spacing: 0.28em;
        text-transform: uppercase;
        color: #d4a12a;
        opacity: 0.9;
      }
      /* The flagship button is deliberately larger than anything else on the
         page — a hero with two same-sized buttons has no hero. */
      .home-action-hero {
        font-size: 1.12rem;
        padding: 18px 40px;
        box-shadow: 0 10px 34px rgba(245, 197, 24, 0.28);
      }
      .home-action-primary {
        color: #080a12;
        background: #f5c518;
        box-shadow:
          0 0 0 1px rgba(255, 255, 255, 0.12),
          0 16px 34px rgba(245, 197, 24, 0.2);
      }

      .home-action-primary:hover {
        transform: translateY(-2px);
        box-shadow:
          0 0 0 1px rgba(255, 255, 255, 0.18),
          0 20px 42px rgba(245, 197, 24, 0.28);
      }

      .home-action-secondary {
        color: #f0e6d3;
        background: rgba(255, 255, 255, 0.07);
        box-shadow:
          0 0 0 1px rgba(255, 255, 255, 0.1),
          0 10px 26px rgba(0, 0, 0, 0.22);
      }

      .home-action-secondary:hover {
        transform: translateY(-2px);
        background: rgba(255, 255, 255, 0.1);
        box-shadow:
          0 0 0 1px rgba(255, 255, 255, 0.16),
          0 16px 34px rgba(0, 0, 0, 0.28);
      }

      .home-stat-strip {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 10px;
        max-width: 560px;
        width: 100%;
      }

      .home-stat {
        min-height: 74px;
        border-radius: 10px;
        padding: 12px;
        background: rgba(255, 255, 255, 0.055);
        box-shadow:
          0 0 0 1px rgba(255, 255, 255, 0.08),
          0 12px 32px rgba(0, 0, 0, 0.18);
      }

      .home-stat-value {
        color: #f5c518;
        font-family: ${FONT.heading};
        font-size: 22px;
        line-height: 1;
        font-variant-numeric: tabular-nums;
      }

      .home-stat-label {
        margin-top: 7px;
        color: #8d96b8;
        font-family: ${FONT.body};
        font-size: 9px;
        letter-spacing: 1.4px;
        text-transform: uppercase;
      }

      .home-section-header {
        width: min(1200px, 100%);
        display: flex;
        align-items: end;
        justify-content: space-between;
        gap: 18px;
        margin: 0 0 14px;
      }

      .home-section-kicker {
        color: #f5c518;
        font-family: ${FONT.body};
        font-size: 10px;
        letter-spacing: 2.4px;
        text-transform: uppercase;
        margin: 0 0 6px;
      }

      .home-section-title {
        color: #f0e6d3;
        font-family: ${FONT.heading};
        font-size: clamp(24px, 3vw, 34px);
        margin: 0;
        letter-spacing: 0;
      }

      .home-section-note {
        color: #7d86a6;
        font-family: ${FONT.body};
        font-size: 12px;
        margin: 0;
      }

      .filter-pill,
      .game-card-button {
        transition-duration: 170ms;
        transition-timing-function: ease-out;
      }

      .filter-pill,
      .game-card-button {
        transition-property: scale, transform, box-shadow, background-color, color;
      }

      .filter-pill {
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      .filter-pill:hover {
        transform: translateY(-1px);
      }

      .game-card-button:hover {
        transform: translateY(-4px);
        background: rgba(255, 255, 255, 0.075);
        box-shadow:
          0 0 0 1px var(--game-color-hover),
          0 16px 46px var(--game-glow);
      }

      .game-card-button:focus-visible,
      .filter-pill:focus-visible,
      .home-action:focus-visible {
        outline: 2px solid #f5c518;
        outline-offset: 3px;
      }

      .home-library-grid {
        animation: homeFadeUp 380ms ease-out both;
      }

      @keyframes homeFadeUp {
        from {
          opacity: 0;
          transform: translateY(12px);
          filter: blur(4px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
          filter: blur(0);
        }
      }

      @media (max-width: 980px) {
        .home-hero-copy {
          min-height: auto;
          padding-top: 10px;
        }
      }

      @media (max-width: 640px) {
        .home-stat-strip {
          grid-template-columns: 1fr;
        }

        .home-actions {
          display: grid;
          grid-template-columns: 1fr;
        }

        .home-action {
          width: 100%;
        }

        .home-section-header {
          align-items: start;
          flex-direction: column;
        }
      }
    `}</style>
  );
}

function HomeSectionHeader({ kicker, title, note }) {
  return (
    <div className="home-section-header">
      <div>
        <p className="home-section-kicker">{kicker}</p>
        <h2 className="home-section-title">{title}</h2>
      </div>
      {note && <p className="home-section-note">{note}</p>}
    </div>
  );
}

// ─── Filter Bar + Search ──────────────────────────────────────────
function FilterBar({ filters, onFilterChange, searchQuery, onSearchChange, resultCount, totalCount }) {
  const pillStyle = (active) => ({
    fontSize: '10px',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    padding: '0 13px',
    minHeight: '40px',
    borderRadius: '16px',
    cursor: 'pointer',
    border: 'none',
    background: active ? 'rgba(245,197,24,0.15)' : 'transparent',
    color: active ? '#f5c518' : '#666',
    fontFamily: FONT.body,
    whiteSpace: 'nowrap',
    boxShadow: active
      ? '0 0 0 1px rgba(245,197,24,0.35), 0 8px 20px rgba(245,197,24,0.08)'
      : '0 0 0 1px rgba(255,255,255,0.08)',
  });

  const hasFilters = filters.source !== 'All' || filters.difficulty !== 'All' || filters.tone !== 'All' || searchQuery;

  const sourceOptions = ['All', 'Ramayana', 'Mahabharata', 'Puranas', 'Krishna Leela', 'Sanskrit', 'Vedic Math', 'Hindu Pantheon', 'Vedic Cosmology', 'Ayurveda', 'Bhagavad Gita', 'Buddhism', 'Jyotish'];
  const difficultyOptions = ['All', 'Easy', 'Medium', 'Hard'];
  const toneOptions = ['All', 'Epic', 'Strategic', 'Playful'];

  return (
    <div style={{ marginBottom: '24px', width: '100%', maxWidth: '1200px' }}>
      {/* Search */}
      <div style={{ marginBottom: '12px' }}>
        <input
          type="text"
          placeholder="Search games..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          style={{
            width: '100%',
            maxWidth: '400px',
            minHeight: '44px',
            padding: '0 16px',
            borderRadius: '8px',
            border: '1px solid rgba(245,197,24,0.2)',
            background: 'rgba(255,255,255,0.04)',
            color: '#f0e6d3',
            fontSize: '14px',
            fontFamily: FONT.body,
            outline: 'none',
          }}
        />
      </div>
      {/* Source pills */}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
        {sourceOptions.map(opt => (
          <button key={opt} type="button" className="filter-pill" style={pillStyle(filters.source === opt)}
            onClick={() => { onFilterChange({ ...filters, source: opt }); analytics.engagement('filter_change', `source:${opt}`, 1); }}>
            {opt}
          </button>
        ))}
      </div>
      {/* Difficulty + Tone pills */}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ color: '#555', fontSize: '9px', letterSpacing: '1px', textTransform: 'uppercase', fontFamily: FONT.body, marginRight: '4px' }}>Difficulty:</span>
        {difficultyOptions.map(opt => (
          <button key={`d-${opt}`} type="button" className="filter-pill" style={pillStyle(filters.difficulty === opt)}
            onClick={() => { onFilterChange({ ...filters, difficulty: opt }); analytics.engagement('filter_change', `difficulty:${opt}`, 1); }}>
            {opt}
          </button>
        ))}
        <span style={{ color: '#555', fontSize: '9px', letterSpacing: '1px', textTransform: 'uppercase', fontFamily: FONT.body, marginLeft: '12px', marginRight: '4px' }}>Tone:</span>
        {toneOptions.map(opt => (
          <button key={`t-${opt}`} type="button" className="filter-pill" style={pillStyle(filters.tone === opt)}
            onClick={() => { onFilterChange({ ...filters, tone: opt }); analytics.engagement('filter_change', `tone:${opt}`, 1); }}>
            {opt}
          </button>
        ))}
        {hasFilters && (
          <button type="button" className="filter-pill" style={{ ...pillStyle(false), color: '#f5c518', marginLeft: '8px' }}
            onClick={() => { onFilterChange({ source: 'All', difficulty: 'All', tone: 'All' }); onSearchChange(''); }}>
            Clear ({resultCount}/{totalCount})
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Navigation Bar ────────────────────────────────────────────────
function NavBar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const isGame = location.pathname.startsWith('/games/');

  const navStyle = {
    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0 24px', height: '56px',
    background: scrolled || isGame ? 'rgba(10,10,26,0.95)' : 'rgba(10,10,26,0.8)',
    backdropFilter: 'blur(12px)',
    borderBottom: '1px solid rgba(245,197,24,0.15)',
    fontFamily: FONT.heading,
    transition: 'background 0.3s ease',
  };

  const linkStyle = (path) => {
    const active = location.pathname === path || (path === '/' && location.pathname === '');
    return {
      color: active ? '#f5c518' : '#8888aa',
      textDecoration: 'none', fontSize: '13px', letterSpacing: '1.5px',
      textTransform: 'uppercase', cursor: 'pointer', padding: '8px 16px',
      borderRadius: '6px',
      transitionProperty: 'background-color, color, box-shadow',
      transitionDuration: '160ms',
      transitionTimingFunction: 'ease-out',
      background: active ? 'rgba(245,197,24,0.1)' : 'transparent',
    };
  };

  const handleNav = (path) => {
    setMenuOpen(false);
    navigate(path);
    window.scrollTo(0, 0);
  };

  return (
    <nav style={navStyle}>
      <div onClick={() => handleNav('/')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span style={{ color: '#f5c518', fontSize: '20px', fontWeight: 'bold', letterSpacing: '2px', fontFamily: FONT.heading }}>
          VEDIC GAMES
        </span>
      </div>
      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }} className="nav-desktop">
        <span style={linkStyle('/')} onClick={() => handleNav('/')}>Home</span>
        <span style={linkStyle('/ramayana')} onClick={() => handleNav('/ramayana')}>Ramayana</span>
        <span style={linkStyle('/ramayana-movie')} onClick={() => handleNav('/ramayana-movie')}>Movie</span>
        <span style={linkStyle('/games')} onClick={() => handleNav('/games')}>Games</span>
        <span style={linkStyle('/about')} onClick={() => handleNav('/about')}>About</span>
      </div>
      <div style={{ display: 'none', cursor: 'pointer', padding: '8px' }} className="nav-mobile-toggle" onClick={() => setMenuOpen(!menuOpen)}>
        <div style={{ width: '24px', height: '2px', background: '#f5c518', marginBottom: '5px' }} />
        <div style={{ width: '24px', height: '2px', background: '#f5c518', marginBottom: '5px' }} />
        <div style={{ width: '24px', height: '2px', background: '#f5c518' }} />
      </div>
      {menuOpen && (
        <div style={{ position: 'absolute', top: '56px', left: 0, right: 0, background: 'rgba(10,10,26,0.98)', padding: '16px 24px', borderBottom: '1px solid rgba(245,197,24,0.15)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <span style={linkStyle('/')} onClick={() => handleNav('/')}>Home</span>
          <span style={linkStyle('/ramayana')} onClick={() => handleNav('/ramayana')}>Ramayana</span>
          <span style={linkStyle('/ramayana-movie')} onClick={() => handleNav('/ramayana-movie')}>Movie</span>
          <span style={linkStyle('/games')} onClick={() => handleNav('/games')}>Games</span>
          <span style={linkStyle('/about')} onClick={() => handleNav('/about')}>About</span>
        </div>
      )}
      <style>{`
        @media (max-width: 640px) {
          .nav-desktop { display: none !important; }
          .nav-mobile-toggle { display: block !important; }
        }
      `}</style>
    </nav>
  );
}

// ─── Dynamic SEO per page/game ─────────────────────────────────────
function setSingletonHeadElement(selector, tagName, attributes) {
  const existing = Array.from(document.head.querySelectorAll(selector));
  const element = existing.shift() || document.createElement(tagName);
  for (const [name, value] of Object.entries(attributes)) element.setAttribute(name, value);
  if (!element.parentNode) document.head.appendChild(element);
  for (const duplicate of existing) duplicate.remove();
  return element;
}

// The non-game routes each need their own title and canonical; sharing the
// home page's made /games, /ramayana and /about read as duplicates of / to
// search engines. Keep these in step with scripts/prerender.js, which bakes
// the same values into the shipped HTML.
const STATIC_PAGE_SEO = {
  '/': {
    title: 'Vedic Games - Free Hindu Mythology Action Browser Games',
    description: `Play ${games.length}+ free browser games based on Hindu mythology, Sanskrit, Vedic Math, Ayurveda, Buddhism & Bhagavad Gita. Mythic action, puzzles, and arcade runs in your browser.`,
    socialTitle: `Vedic Games - ${games.length}+ Free Hindu Mythology Action Browser Games`,
  },
  '/games': {
    title: `All ${games.length} Games - Free Hindu Mythology Browser Games | Vedic Games`,
    description: `Browse all ${games.length} free Vedic Games: Ramayana archery and runners, Mahabharata battles, Sanskrit and Vedic Math arcade puzzles, Bhagavad Gita and Buddhism games. No signup, no download.`,
    socialTitle: `All ${games.length} Free Hindu Mythology Browser Games`,
  },
  '/ramayana': {
    title: 'Ramayana Games - 21-Game Journey Through the Epic | Vedic Games',
    description: "Play the full Ramayana as a 21-game journey, from Rama's exile to the battle of Lanka. Saw the 2026 Ramayana movie? Play every scene of Part 1 and what comes next in Part 2. Free, no download.",
    socialTitle: 'Ramayana Games - The Full 21-Game Journey',
  },
  [RAMAYANA_MOVIE.page.path]: {
    title: RAMAYANA_MOVIE.page.title,
    description: RAMAYANA_MOVIE.page.description,
    socialTitle: RAMAYANA_MOVIE.page.socialTitle,
  },
  '/about': {
    title: 'About Vedic Games - Free Hindu Mythology Browser Games',
    description: 'Vedic Games is a free collection of browser games teaching Hindu mythology, Sanskrit, Vedic Math, Ayurveda, Buddhism and the Bhagavad Gita through play. No signup, no ads, no downloads.',
    socialTitle: 'About Vedic Games',
  },
};

// `page` passes a route's own title, description, socialTitle and canonical
// for pages that are neither a registry game nor in STATIC_PAGE_SEO.
function PageSEO({ game, page: routePage }) {
  const location = useLocation();
  const srcLabel = game?.source === 'Puranas / Vedic' ? 'Puranas' : game?.source;
  const route = location.pathname.replace(/\/+$/, '') || '/';
  const page = routePage || STATIC_PAGE_SEO[route] || STATIC_PAGE_SEO['/'];
  const pageCanonical = routePage?.canonical
    || (STATIC_PAGE_SEO[route] ? `https://vedicgame.com${route === '/' ? '/' : route}` : 'https://vedicgame.com/');

  const title = game
    ? `${game.title} - Free ${game.genre} Game | Vedic Games`
    : page.title;
  const description = game
    ? `Play ${game.title} - ${game.subtitle}. ${game.description} Free browser game based on ${srcLabel}. No downloads required.`
    : page.description;
  const socialTitle = game
    ? `${game.title} - ${game.subtitle} | Vedic Games`
    : page.socialTitle;
  const socialDescription = game?.description || description;
  // Games without a bespoke card set ogImage explicitly. Defaulting to
  // /og/<id>.png for those pointed social scrapers at a URL that does not
  // exist, and S3 answers a missing key with the HTML shell, not an image.
  const image = game
    ? `https://vedicgame.com${game.ogImage || `/og/${game.id}.png`}`
    : 'https://vedicgame.com/og-image.png';
  const canonical = game ? `https://vedicgame.com/games/${game.id}` : pageCanonical;
  const imageWidth = String(game?.ogImageWidth || 1200);
  const imageHeight = String(game?.ogImageHeight || 630);

  useEffect(() => {
    const titleNodes = Array.from(document.head.querySelectorAll('title'));
    const titleNode = titleNodes.shift() || document.createElement('title');
    titleNode.textContent = title;
    if (!titleNode.parentNode) document.head.appendChild(titleNode);
    for (const duplicate of titleNodes) duplicate.remove();

    setSingletonHeadElement('meta[name="description"]', 'meta', { name: 'description', content: description });
    setSingletonHeadElement('meta[property="og:title"]', 'meta', { property: 'og:title', content: socialTitle });
    setSingletonHeadElement('meta[property="og:description"]', 'meta', { property: 'og:description', content: socialDescription });
    setSingletonHeadElement('meta[property="og:image"]', 'meta', { property: 'og:image', content: image });
    setSingletonHeadElement('meta[property="og:image:width"]', 'meta', { property: 'og:image:width', content: imageWidth });
    setSingletonHeadElement('meta[property="og:image:height"]', 'meta', { property: 'og:image:height', content: imageHeight });
    setSingletonHeadElement('meta[property="og:url"]', 'meta', { property: 'og:url', content: canonical });
    setSingletonHeadElement('meta[name="twitter:title"]', 'meta', { name: 'twitter:title', content: socialTitle });
    setSingletonHeadElement('meta[name="twitter:description"]', 'meta', { name: 'twitter:description', content: socialDescription });
    setSingletonHeadElement('meta[name="twitter:image"]', 'meta', { name: 'twitter:image', content: image });
    setSingletonHeadElement('link[rel="canonical"]', 'link', { rel: 'canonical', href: canonical });
    // NotFound sets noindex. Without this, navigating from a 404 to a real
    // page inside the SPA leaves the whole session flagged noindex.
    setSingletonHeadElement('meta[name="robots"]', 'meta', {
      name: 'robots', content: 'index, follow, max-snippet:-1, max-image-preview:large',
    });
  }, [canonical, description, image, imageHeight, imageWidth, socialDescription, socialTitle, title]);

  return null;
}

// ─── Game Card ─────────────────────────────────────────────────────
const tagPill = (bg, color, text) => (
  <span key={text} style={{
    fontSize: '9px', letterSpacing: '1px', textTransform: 'uppercase',
    background: bg, color: color, padding: '3px 8px',
    borderRadius: '4px', fontFamily: FONT.body,
  }}>
    {text}
  </span>
);

function GameCard({ game, onClick }) {
  const Icon = GAME_ICONS[game.id];
  const srcColor = SOURCE_TAG_COLORS[game.source] || { bg: 'rgba(255,255,255,0.06)', text: '#8888aa' };
  const diffColor = DIFFICULTY_TAG_COLORS[game.difficulty] || { bg: 'rgba(255,255,255,0.06)', text: '#8888aa' };
  const sourceLabel = game.source === 'Puranas / Vedic' ? 'Puranas' : game.source;

  return (
    <button
      type="button"
      className="game-card-button"
      onClick={onClick}
      style={{
        '--game-color': game.color,
        '--game-color-hover': `${game.color}88`,
        '--game-glow': `${game.color}22`,
        background: 'rgba(255,255,255,0.04)',
        border: 'none',
        borderRadius: '12px', padding: '24px', cursor: 'pointer',
        width: '100%',
        textAlign: 'left',
        minHeight: '100%',
        position: 'relative', overflow: 'hidden',
        boxSizing: 'border-box', alignSelf: 'start',
        boxShadow: `0 0 0 1px ${game.color}33, 0 10px 34px rgba(0,0,0,0.18)`,
      }}
    >
      <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
        {game.isNew && (
          <span style={{ fontSize: '8px', fontWeight: 'bold', letterSpacing: '1px', textTransform: 'uppercase', background: '#e74c3c', color: '#fff', padding: '3px 7px', borderRadius: '4px', fontFamily: FONT.body }}>NEW</span>
        )}
        {tagPill(srcColor.bg, srcColor.text, sourceLabel)}
        {tagPill(`${game.color}22`, game.color, game.genre)}
        {tagPill(diffColor.bg, diffColor.text, game.difficulty)}
      </div>
      <div style={{ marginBottom: '12px' }}>{Icon && <Icon size={48} />}</div>
      <h2 style={{ color: game.color, margin: '0 0 4px', fontSize: '18px', fontFamily: FONT.heading }}>{game.title}</h2>
      <p style={{ color: '#8888aa', margin: '0 0 10px', fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', fontFamily: FONT.body }}>{game.subtitle}</p>
      <p style={{ color: '#999', margin: 0, fontSize: '12.5px', lineHeight: 1.6, fontFamily: FONT.body }}>{game.description}</p>
      <div style={{ marginTop: '14px', color: game.color, fontSize: '12px', fontWeight: 'bold', letterSpacing: '1.5px', fontFamily: FONT.body }}>PLAY NOW &rarr;</div>
    </button>
  );
}

// ─── Home Page ─────────────────────────────────────────────────────
function Home() {
  const [filters, setFilters] = useState({ source: 'All', difficulty: 'All', tone: 'All' });
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => { analytics.pageView('Home', '/'); }, []);

  const filteredGames = filterGames(games, filters, searchQuery);
  const sourceCount = new Set(games.map(g => g.source)).size;
  const genreCount = new Set(games.map(g => g.genre)).size;

  const handleSelectGame = useCallback((gameId) => {
    navigate(`/games/${gameId}`);
    window.scrollTo(0, 0);
  }, [navigate]);

  const scrollToLibrary = useCallback(() => {
    document.getElementById('game-library')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  return (
    <main className="home-shell" style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #070812 0%, #1a1024 43%, #071b24 100%)',
      fontFamily: FONT.heading, padding: '80px 20px 60px',
    }}>
      <HomePolishStyles />

      <section className="home-hero" aria-label="Vedic Games">
        <div className="home-hero-copy">
          <div className="home-eyebrow">No download. No lobby. Play now.</div>
          {/*
            The hero sells ONE game. Kuru of Duty is the flagship and everything
            else is library — four sibling buttons of equal weight made the
            homepage a menu, and a menu asks the visitor to do research before
            they can play anything.
          */}
          <p className="home-hero-kicker">The flagship</p>
          <h1 className="home-hero-title" style={{ fontFamily: FONT.heading }}>
            Kuru of Duty
          </h1>
          <p className="home-hero-lede">
            A first-person war on the plain of Kurukshetra. Forty thousand soldiers fight around you
            in real time while nine named Kaurava commanders hold the field from their chariots.
            Ride with Krishna, draw the Gandiva, and loose the Agneyastra when the line breaks.
            No download — it runs in this tab.
          </p>
          <div className="home-actions">
            <button type="button" className="home-action home-action-primary home-action-hero" onClick={() => handleSelectGame('kuru-of-duty')}>
              ⚔️ Play Kuru of Duty
            </button>
            <button type="button" className="home-action home-action-secondary" onClick={scrollToLibrary}>
              Browse all {games.length} games
            </button>
          </div>
          <div className="home-stat-strip" aria-label="Game catalog stats">
            {[
              [games.length, 'Free games'],
              [sourceCount, 'Story sources'],
              [genreCount, 'Arcade genres'],
            ].map(([value, label]) => (
              <div className="home-stat" key={label}>
                <div className="home-stat-value">{value}</div>
                <div className="home-stat-label">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <HomeSectionHeader
        kicker="Game library"
        title="Pick a game"
        note={`${filteredGames.length}/${games.length} games shown`}
      />
      <FilterBar filters={filters} onFilterChange={setFilters} searchQuery={searchQuery} onSearchChange={setSearchQuery} resultCount={filteredGames.length} totalCount={games.length} />

      {/* Game Grid */}
      {filteredGames.length > 0 ? (
        <div id="game-library" className="home-library-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px', maxWidth: '1200px', width: '100%' }}>
          {filteredGames.map((game) => (
            <GameCard key={game.id} game={game} onClick={() => handleSelectGame(game.id)} />
          ))}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#666', fontSize: '15px', fontFamily: FONT.body }}>
          <p style={{ margin: '0 0 16px' }}>No games match these filters</p>
          <span style={{ color: '#f5c518', cursor: 'pointer', fontSize: '13px', letterSpacing: '1px' }}
            onClick={() => { setFilters({ source: 'All', difficulty: 'All', tone: 'All' }); setSearchQuery(''); }}>
            Clear Filters
          </span>
        </div>
      )}

      <div style={{ maxWidth: '800px', textAlign: 'center', marginTop: '48px', color: '#444', fontSize: '12px', lineHeight: 1.8, fontFamily: FONT.body }}>
        <p>Vedic Games offers free browser-based Hindu mythology games featuring stories from the Ramayana, Mahabharata, Puranas, and Krishna Leela. Jump into action, puzzles, chases, and boss fights - no downloads required.</p>
      </div>
    </main>
  );
}

// ─── Games List Page ───────────────────────────────────────────────
function GamesPage() {
  const [filters, setFilters] = useState({ source: 'All', difficulty: 'All', tone: 'All' });
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => { analytics.pageView('All Games', '/games'); }, []);

  const filteredGames = filterGames(games, filters, searchQuery);

  const handleSelectGame = useCallback((gameId) => {
    navigate(`/games/${gameId}`);
    window.scrollTo(0, 0);
  }, [navigate]);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0a1a 0%, #1a0a2e 50%, #0a1628 100%)',
      fontFamily: FONT.heading, padding: '80px 20px 60px',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
    }}>
      <div style={{ maxWidth: '1200px', width: '100%' }}>
        <h1 style={{ color: '#f5c518', fontSize: '36px', textAlign: 'center', margin: '0 0 8px', letterSpacing: '2px', fontFamily: FONT.heading }}>All Games</h1>
        <p style={{ color: '#8888aa', fontSize: '13px', textAlign: 'center', letterSpacing: '2px', textTransform: 'uppercase', margin: '0 0 32px', fontFamily: FONT.body }}>
          {games.length} Games &middot; {new Set(games.map(g => g.source)).size} Sources &middot; {new Set(games.map(g => g.genre)).size} Genres
        </p>

        <FilterBar filters={filters} onFilterChange={setFilters} searchQuery={searchQuery} onSearchChange={setSearchQuery} resultCount={filteredGames.length} totalCount={games.length} />

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
          {filteredGames.map((game) => (
            <GameCard key={game.id} game={game} onClick={() => handleSelectGame(game.id)} />
          ))}
        </div>

        {filteredGames.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#666', fontFamily: FONT.body }}>
            <p>No games match these filters</p>
            <span style={{ color: '#f5c518', cursor: 'pointer', fontSize: '13px' }}
              onClick={() => { setFilters({ source: 'All', difficulty: 'All', tone: 'All' }); setSearchQuery(''); }}>
              Clear Filters
            </span>
          </div>
        )}

        {/* SEO game list table */}
        <div style={{ marginTop: '60px', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT.body, fontSize: '12px', color: '#888' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                {['Game', 'Source', 'Genre', 'Difficulty'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: '#f5c518', letterSpacing: '1px', textTransform: 'uppercase', fontSize: '10px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {games.map((g) => (
                <tr key={g.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer' }}
                  onClick={() => handleSelectGame(g.id)}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={{ padding: '10px 12px', color: g.color, fontWeight: 'bold' }}>{g.title}</td>
                  <td style={{ padding: '10px 12px' }}>{g.source}</td>
                  <td style={{ padding: '10px 12px' }}>{g.genre}</td>
                  <td style={{ padding: '10px 12px' }}>{g.difficulty}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── About Page ────────────────────────────────────────────────────
function AboutPage() {
  useEffect(() => { analytics.pageView('About', '/about'); }, []);

  const sectionStyle = { marginBottom: '40px' };
  const headingStyle = { color: '#f5c518', fontSize: '20px', margin: '0 0 16px', letterSpacing: '1px', fontFamily: FONT.heading };
  const textStyle = { color: '#aaa', fontSize: '14px', lineHeight: 1.8, margin: '0 0 12px', fontFamily: FONT.body };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0a1a 0%, #1a0a2e 50%, #0a1628 100%)',
      fontFamily: FONT.heading, padding: '80px 20px 60px',
    }}>
      <div style={{ maxWidth: '720px', margin: '0 auto' }}>
        <h1 style={{ color: '#f5c518', fontSize: '36px', textAlign: 'center', margin: '0 0 8px', letterSpacing: '2px', fontFamily: FONT.heading }}>About Vedic Games</h1>
        <p style={{ color: '#8888aa', fontSize: '13px', textAlign: 'center', letterSpacing: '2px', textTransform: 'uppercase', margin: '0 0 48px', fontFamily: FONT.body }}>Our Mission & Story</p>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>What is Vedic Games?</h2>
          <p style={textStyle}>Vedic Games is a free collection of browser-based games rooted in Hindu mythology. Each game turns a story from the Ramayana, Mahabharata, Puranas, or Krishna Leela into a playable chase, fight, puzzle, or arcade challenge.</p>
          <p style={textStyle}>Our mission is to turn Hindu mythology into fast, replayable browser games. We focus on the parts of these stories that naturally become chases, fights, puzzles, runs, and boss battles.</p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>Our Games</h2>
          <p style={textStyle}>We offer {games.length} unique games spanning {new Set(games.map(g => g.genre)).size} different genres, ensuring no two games feel alike:</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px', marginTop: '12px' }}>
            {games.map(g => (
              <div key={g.id} style={{ padding: '6px 0' }}>
                <span style={{ color: g.color, fontSize: '13px', fontWeight: 'bold', fontFamily: FONT.heading }}>{g.title}</span>
                <span style={{ color: '#666', fontSize: '12px', fontFamily: FONT.body }}> - {g.genre}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>Cultural Sensitivity</h2>
          <p style={textStyle}>Hinduism is a living, actively practiced religion. We treat all deities with deep reverence. Players take on the roles of heroes and devotees, never gods themselves. Every game mechanic is grounded in actual scripture and traditional narratives.</p>
          <p style={textStyle}>Our art styles draw from India's rich painting traditions: Madhubani, Pichwai, Tanjore, Pahari miniatures, and Chola bronze aesthetics.</p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>Mythology Sources</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '8px' }}>
            {[
              { name: 'Ramayana', desc: "Valmiki's ancient epic of Lord Rama's journey. Plus a full 21-game Ramayana Journey.", count: games.filter(g => g.source === 'Ramayana').length },
              { name: 'Mahabharata', desc: "The great epic of the Pandavas and Kauravas - Chakravyuha, Kurukshetra War, Dharma Court.", count: games.filter(g => g.source === 'Mahabharata').length },
              { name: 'Puranas & Vedic', desc: "Cosmic stories - Samudra Manthan and Dashavatara spanning all cosmic ages.", count: games.filter(g => g.source.startsWith('Puranas')).length },
              { name: 'Brainy', desc: "Sanskrit, Vedic Math, Ayurveda, Bhagavad Gita, Buddhism, and more.", count: games.filter(g => ['Sanskrit', 'Vedic Math', 'Hindu Pantheon', 'Vedic Cosmology', 'Ayurveda', 'Bhagavad Gita', 'Buddhism', 'Jyotish'].includes(g.source)).length },
            ].map(s => (
              <div key={s.name} style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '8px', padding: '16px', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <h3 style={{ color: '#f5c518', margin: 0, fontSize: '15px', fontFamily: FONT.heading }}>{s.name}</h3>
                  <span style={{ color: '#666', fontSize: '11px', fontFamily: FONT.body }}>{s.count} game{s.count > 1 ? 's' : ''}</span>
                </div>
                <p style={{ color: '#888', margin: 0, fontSize: '13px', lineHeight: 1.7, fontFamily: FONT.body }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>Technology</h2>
          <p style={textStyle}>All games are built with HTML5 Canvas and JavaScript, running entirely in your browser. No downloads, no plugins, no sign-ups required.</p>
        </div>

        <div style={{ textAlign: 'center', padding: '32px 0', marginTop: '20px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <p style={{ color: '#555', fontSize: '12px', letterSpacing: '1px', fontFamily: FONT.body }}>Vedic Games &middot; Free Hindu Mythology Browser Games</p>
          <p style={{ color: '#444', fontSize: '11px', marginTop: '4px', fontFamily: FONT.body }}>Made with reverence for the stories that shaped a civilization</p>
        </div>
      </div>
    </div>
  );
}


/*
  Retired slugs, kept resolvable. A shared link is permanent even when a name
  is not, and the catch-all route silently redirects an unknown slug to the
  home page — which looks exactly like the game was deleted.
*/
const GAME_SLUG_ALIASES = {
  'kurukshetra-duty': 'kuru-of-duty',
};

// ─── Game Page (Direct Player) ────────────────────────────────────
function GamePage() {
  const { gameId: rawGameId, resultId } = useParams();
  const gameId = GAME_SLUG_ALIASES[rawGameId] || rawGameId;
  const sessionStart = useRef(null);
  const autoStartedGameRef = useRef(null);
  const navigate = useNavigate();

  const game = games.find(g => g.id === gameId);
  const GameComponent = GAME_COMPONENTS[gameId];

  // A retired slug plays fine, but leaving the URL as-is gives search engines
  // two addresses for one game. Rewrite it to the canonical one.
  useEffect(() => {
    // Carries the query string across, for the same reason HashRedirect does.
    if (rawGameId !== gameId) {
      navigate(`/games/${gameId}${window.location.search || ''}`, { replace: true });
    }
  }, [rawGameId, gameId, navigate]);

  useEffect(() => {
    sessionStart.current = null;
    if (game && autoStartedGameRef.current !== gameId) {
      autoStartedGameRef.current = gameId;

      // Universal replay signal.
      //
      // Only 8 of 27 game components call analytics.replay() themselves, so
      // per-game replay data was never comparable: 19 games reported zero
      // replays because they cannot report one, not because nobody replayed
      // them. Ranking games on that number would have been ranking them on
      // which ones happen to be instrumented.
      //
      // Returning to a game already played this session is a replay by any
      // useful definition, and it is observable here for every game at once.
      // In-game restarts still fire their own event where wired; this catches
      // the rest.
      const playedKey = 'vg.played';
      let played = [];
      try {
        played = JSON.parse(window.sessionStorage.getItem(playedKey) || '[]');
      } catch { played = []; }
      if (Array.isArray(played) && played.includes(gameId)) {
        analytics.event('game_replay', 'Game', gameId, 1);
      } else {
        try {
          window.sessionStorage.setItem(
            playedKey,
            JSON.stringify([...(Array.isArray(played) ? played : []), gameId].slice(-60))
          );
        } catch { /* private mode — replay signal degrades, game does not */ }
      }

      analytics.gameStart(gameId, game.title);
      analytics.engagement('game_click', gameId, 1);
      sessionStart.current = Date.now();
      window.scrollTo(0, 0);
    }
  }, [gameId, game]);

  const handleBack = useCallback(() => {
    if (sessionStart.current && game) {
      const seconds = Math.round((Date.now() - sessionStart.current) / 1000);
      analytics.gameEnd(gameId, game.title, seconds, 0, 'exit');
      analytics.engagement('game_session_duration', gameId, seconds);
    }
    navigate('/games');
  }, [gameId, game, navigate]);

  if (!game || !GameComponent || (resultId && !GAME_RESULT_IDS[gameId]?.includes(resultId))) {
    return <NotFound />;
  }

  return (
    <Suspense fallback={<Loading />}>
      <div>
        <PageSEO game={game} />
        {/*
          The game itself is a full-bleed canvas with no room for a heading, so
          the page rendered with zero H1 — nothing named it for crawlers or a
          screen reader. Off-screen rather than display:none, which assistive
          tech skips.
        */}
        <h1 style={{
          position: 'absolute', width: 1, height: 1, overflow: 'hidden',
          clip: 'rect(0 0 0 0)', clipPath: 'inset(50%)', whiteSpace: 'nowrap',
        }}>
          {game.title} — {game.subtitle}
        </h1>
        <NavBar />
        <button onClick={handleBack} style={{
          position: 'fixed', top: '68px', left: '16px', zIndex: 1000,
          background: 'rgba(0,0,0,0.7)', color: '#f5c518',
          border: '1px solid rgba(245,197,24,0.3)', borderRadius: '8px',
          padding: '8px 16px', cursor: 'pointer', fontFamily: FONT.heading,
          fontSize: '13px', letterSpacing: '1px',
        }}>
          &larr; Back
        </button>
        <div style={{ paddingTop: '56px' }}>
          <GameComponent resultId={resultId} />
        </div>
        {/* Asks what to build next, once per session, after real play time.
            Inline below the canvas — never an overlay on a running game. */}
        <GameFeedback gameId={gameId} gameTitle={game.title} />
        <MovieTie gameId={gameId} />
      </div>
    </Suspense>
  );
}

// ─── Ramayana movie (2026) ─────────────────────────────────────────
// Content lives in ramayanaMovie.json so scripts/prerender.js can bake the
// same text and FAQ schema into the shipped HTML.
const movieText = { color: '#aaa', fontSize: '14px', lineHeight: 1.8, margin: '0 0 12px', fontFamily: FONT.body };
const movieHeading = { color: '#f5c518', fontSize: '20px', margin: '0 0 16px', letterSpacing: '1px', fontFamily: FONT.heading };
const movieLink = { color: '#ff9933', fontFamily: FONT.body };

function SpaLink({ to, children }) {
  const navigate = useNavigate();
  return (
    <a href={to} style={movieLink} onClick={(e) => { e.preventDefault(); navigate(to); window.scrollTo(0, 0); }}>
      {children}
    </a>
  );
}

function MovieFaq({ items }) {
  return items.map(([q, a]) => (
    <div key={q} style={{ marginBottom: '16px' }}>
      <h3 style={{ color: '#e8e8f0', fontSize: '15px', margin: '0 0 6px', fontFamily: FONT.heading }}>{q}</h3>
      <p style={movieText}>{a}</p>
    </div>
  ));
}

function MovieScenes({ scenes }) {
  return (
    <ol style={{ paddingLeft: '20px', margin: 0 }}>
      {scenes.map((s) => (
        <li key={s.scene} style={{ marginBottom: '18px', color: '#aaa' }}>
          <strong style={{ color: '#e8e8f0', fontFamily: FONT.heading }}>{s.scene}</strong>
          <p style={{ ...movieText, margin: '4px 0' }}>{s.story}</p>
          <span style={{ fontSize: '13px', fontFamily: FONT.body }}>
            Play: <SpaLink to={`/ramayana/${s.chapterGame}`}>{s.play}</SpaLink>
            {s.game && (
              <> · or the quick game <SpaLink to={`/games/${s.game}`}>{games.find(g => g.id === s.game)?.title}</SpaLink></>
            )}
          </span>
        </li>
      ))}
    </ol>
  );
}

function RamayanaMoviePage() {
  const m = RAMAYANA_MOVIE.page;
  useEffect(() => { analytics.pageView('Ramayana Movie', m.path); }, [m.path]);
  const section = { marginBottom: '40px' };
  const cell = { padding: '8px 12px 8px 0', verticalAlign: 'top' };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0a1a 0%, #1a0a2e 50%, #0a1628 100%)',
      fontFamily: FONT.heading, padding: '80px 20px 60px',
    }}>
      <div style={{ maxWidth: '720px', margin: '0 auto' }}>
        <h1 style={{ color: '#f5c518', fontSize: '30px', textAlign: 'center', margin: '0 0 24px', letterSpacing: '1px', fontFamily: FONT.heading }}>{m.h1}</h1>
        <p style={movieText}>{m.intro}</p>

        <div style={section}>
          <h2 style={movieHeading}>The film at a glance</h2>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT.body, fontSize: '14px' }}>
            <tbody>
              {m.facts.map(([k, v]) => (
                <tr key={k} style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  <th style={{ ...cell, textAlign: 'left', color: '#f5c518' }}>{k}</th>
                  <td style={{ ...cell, color: '#aaa' }}>{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={section}>
          <h2 style={movieHeading}>Ramayana Part 1 story, scene by scene</h2>
          <p style={movieText}>Spoiler note: this follows Valmiki's Ramayana, which the film is based on. The film can change details.</p>
          <MovieScenes scenes={m.part1Scenes} />
        </div>

        <div style={section}>
          <h2 style={movieHeading}>What Ramayana Part 2 will show</h2>
          <p style={movieText}>Part 1 stops at Sita's abduction. The rest of the epic is the story of Part 2. You can play it now.</p>
          <MovieScenes scenes={m.part2Scenes} />
        </div>

        <div style={section}>
          <h2 style={movieHeading}>Cast: who plays who</h2>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT.body, fontSize: '14px' }}>
            <tbody>
              {m.cast.map(([role, actor, gameId]) => (
                <tr key={role} style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  <th style={{ ...cell, textAlign: 'left', color: '#e8e8f0' }}>{role}</th>
                  <td style={{ ...cell, color: '#aaa' }}>{actor}</td>
                  <td style={cell}>
                    {gameId && <SpaLink to={`/games/${gameId}`}>{games.find(g => g.id === gameId)?.title}</SpaLink>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={section}>
          <h2 style={movieHeading}>Ramayana movie FAQ</h2>
          <MovieFaq items={m.faq} />
        </div>

        <p style={{ ...movieText, fontSize: '12px', color: '#777' }}>{m.disclaimer}</p>
        <p style={{ ...movieText, fontSize: '12px', color: '#777' }}>
          Sources: {m.sources.map(([label, url], i) => (
            <span key={url}>{i > 0 && ' · '}<a href={url} rel="nofollow noopener noreferrer" target="_blank" style={{ color: '#8888aa' }}>{label}</a></span>
          ))}
        </p>
      </div>
    </div>
  );
}

// Shown under a game that maps to a scene of the film. Below the canvas, so
// it never covers play.
function MovieTie({ gameId }) {
  const tie = RAMAYANA_MOVIE.games[gameId];
  if (!tie) return null;
  return (
    <section style={{ background: '#0a0a1a', padding: '32px 20px 48px' }}>
      <div style={{ maxWidth: '720px', margin: '0 auto' }}>
        <h2 style={movieHeading}>{tie.heading}</h2>
        <p style={movieText}>{tie.body}</p>
        <p style={movieText}>
          See the <SpaLink to={RAMAYANA_MOVIE.page.path}>Ramayana movie story scene by scene</SpaLink>, or play the <SpaLink to="/ramayana">full 21-game Ramayana journey</SpaLink>.
        </p>
        <MovieFaq items={tie.faq} />
      </div>
    </section>
  );
}

// ─── Ramayana chapter game page (/ramayana/<id>) ───────────────────
// Each of the 21 hub games gets its own URL so a search can land on one
// scene. Copy lives in ramayanaGames.json so scripts/prerender.js bakes the
// same title, text and FAQ schema into build/ramayana/<id>/index.html.
// Keep the title and description below in step with chapterGameSeo there.
const RAMAYANA_SR_ONLY = {
  position: 'absolute', width: 1, height: 1, overflow: 'hidden',
  clip: 'rect(0 0 0 0)', clipPath: 'inset(50%)', whiteSpace: 'nowrap',
};

function RamayanaChapterGamePage() {
  const { chapterGameId } = useParams();
  const navigate = useNavigate();
  const list = RAMAYANA_GAMES.games;
  const index = list.findIndex(g => g.id === chapterGameId);
  if (index === -1) return <NotFound />;

  const g = list[index];
  const prev = list[index - 1];
  const next = list[index + 1];
  const quick = g.quick && games.find(q => q.id === g.quick);
  const seo = {
    title: `${g.title} - Free Ramayana ${g.kanda} Game | Vedic Games`,
    description: g.description,
    socialTitle: `${g.title} - Ramayana Chapter ${g.chapter} | Vedic Games`,
    canonical: `https://vedicgame.com/ramayana/${g.id}`,
  };

  return (
    <div style={{ background: '#0a0a1a' }}>
      <PageSEO game={null} page={seo} />
      <h1 style={RAMAYANA_SR_ONLY}>{g.title}: Ramayana Game ({g.kanda})</h1>
      <NavBar />
      <button onClick={() => navigate(`/ramayana?chapter=${g.chapter}`)} style={{
        position: 'fixed', top: '68px', left: '16px', zIndex: 1000,
        background: 'rgba(0,0,0,0.7)', color: '#f5c518',
        border: '1px solid rgba(245,197,24,0.3)', borderRadius: '8px',
        padding: '8px 16px', cursor: 'pointer', fontFamily: FONT.heading,
        fontSize: '13px', letterSpacing: '1px',
      }}>
        &larr; Chapter {g.chapter}
      </button>
      <div style={{ paddingTop: '56px' }}>
        <Suspense fallback={<Loading />}>
          {/* Keyed so moving to the next chapter game remounts it fresh. */}
          <RamayanaChapterPlayer key={g.id} gameId={g.id} />
        </Suspense>
      </div>
      <section style={{ maxWidth: '720px', margin: '0 auto', padding: '32px 20px 48px' }}>
        <h2 style={movieHeading}>The story: Chapter {g.chapter}, {g.kanda}</h2>
        <p style={movieText}>{g.scene}</p>
        <p style={movieText}><strong style={{ color: '#e8e8f0' }}>How to play:</strong> {g.play}</p>
        {g.movie && (
          <>
            <h2 style={movieHeading}>In the 2026 Ramayana movie</h2>
            <p style={movieText}>{g.movie}</p>
          </>
        )}
        <p style={movieText}>
          {prev && <>Previous: <SpaLink to={`/ramayana/${prev.id}`}>{prev.title}</SpaLink></>}
          {prev && next && ' · '}
          {next && <>Next: <SpaLink to={`/ramayana/${next.id}`}>{next.title}</SpaLink></>}
        </p>
        <p style={movieText}>
          Play the <SpaLink to="/ramayana">full 21-game Ramayana journey</SpaLink>, or see the <SpaLink to={RAMAYANA_MOVIE.page.path}>Ramayana movie story scene by scene</SpaLink>.
          {quick && <> For a quick arcade version, try <SpaLink to={`/games/${quick.id}`}>{quick.title}</SpaLink>.</>}
        </p>
        <h2 style={movieHeading}>Questions about this scene</h2>
        <MovieFaq items={g.faq} />
      </section>
      <Footer />
    </div>
  );
}

// ─── Loading Screen ────────────────────────────────────────────────
function Loading() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: '100vh', background: '#0a0a1a', color: '#f5c518',
      fontFamily: FONT.heading, fontSize: '18px',
    }}>
      Loading...
    </div>
  );
}

// ─── Footer ────────────────────────────────────────────────────────
function Footer() {
  const navigate = useNavigate();

  const handleNav = (path) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  return (
    <footer style={{
      background: '#050510',
      borderTop: '1px solid rgba(245,197,24,0.1)',
      padding: '40px 20px',
      fontFamily: FONT.heading,
    }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto', display: 'flex', flexWrap: 'wrap', gap: '40px', justifyContent: 'space-between' }}>
        <div style={{ maxWidth: '300px' }}>
          <h3 style={{ color: '#f5c518', fontSize: '16px', margin: '0 0 8px', letterSpacing: '2px', fontFamily: FONT.heading }}>VEDIC GAMES</h3>
          <p style={{ color: '#666', fontSize: '12px', lineHeight: 1.7, margin: 0, fontFamily: FONT.body }}>Free browser-based Hindu mythology games. Experience the Ramayana, Mahabharata, Puranas, and Krishna Leela through interactive gameplay.</p>
        </div>

        <div>
          <h4 style={{ color: '#f5c518', fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', margin: '0 0 12px', fontFamily: FONT.body }}>Ramayana Games</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {games.filter(g => g.source === 'Ramayana').map(g => (
              <span key={g.id} onClick={() => handleNav(`/games/${g.id}`)} style={{ color: '#777', fontSize: '12px', cursor: 'pointer', fontFamily: FONT.body }}>{g.title}</span>
            ))}
          </div>
        </div>

        <div>
          <h4 style={{ color: '#f5c518', fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', margin: '0 0 12px', fontFamily: FONT.body }}>Mahabharata Games</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {games.filter(g => g.source === 'Mahabharata').map(g => (
              <span key={g.id} onClick={() => handleNav(`/games/${g.id}`)} style={{ color: '#777', fontSize: '12px', cursor: 'pointer', fontFamily: FONT.body }}>{g.title}</span>
            ))}
          </div>
        </div>

        <div>
          <h4 style={{ color: '#f5c518', fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', margin: '0 0 12px', fontFamily: FONT.body }}>Arcade Puzzles</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {games.filter(g => ['Sanskrit', 'Vedic Math', 'Hindu Pantheon', 'Vedic Cosmology', 'Ayurveda', 'Bhagavad Gita', 'Buddhism', 'Jyotish'].includes(g.source)).map(g => (
              <span key={g.id} onClick={() => handleNav(`/games/${g.id}`)} style={{ color: '#777', fontSize: '12px', cursor: 'pointer', fontFamily: FONT.body }}>{g.title}</span>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '1100px', margin: '32px auto 0', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '20px' }}>
        <p style={{ color: '#888', fontSize: '11px', margin: '0 0 6px', textAlign: 'center', fontWeight: 600, letterSpacing: '1px', fontFamily: FONT.body }}>ALSO FROM SAURAV</p>
        <p style={{ color: '#666', fontSize: '11px', textAlign: 'center', margin: '0 0 16px', fontFamily: FONT.body }}>
          <a href="https://openclawdc.com" style={{ color: '#999', textDecoration: 'none', margin: '0 8px' }}>OpenClawDC</a>&middot;
          <a href="https://markdownme.com" style={{ color: '#999', textDecoration: 'none', margin: '0 8px' }}>MarkdownMe</a>&middot;
          <a href="https://cloudyeti.io" style={{ color: '#999', textDecoration: 'none', margin: '0 8px' }}>CloudYeti</a>&middot;
          <a href="https://earnyeti.com" style={{ color: '#999', textDecoration: 'none', margin: '0 8px' }}>EarnYeti</a>
        </p>
      </div>
      <div style={{ maxWidth: '1100px', margin: '0 auto', textAlign: 'center' }}>
        <p style={{ color: '#444', fontSize: '11px', margin: 0, fontFamily: FONT.body }}>
          Vedic Games &middot; Free Hindu Mythology Browser Games &middot; Ramayana &middot; Mahabharata &middot; Puranas &middot; Sanskrit &middot; Vedic Math &middot; Ayurveda &middot; Bhagavad Gita
        </p>
        <p style={{ color: '#333', fontSize: '10px', marginTop: '4px', fontFamily: FONT.body }}>
          {games.length} free games: {games.map(g => g.title).join(', ')}.
        </p>
      </div>
    </footer>
  );
}

// ─── App ───────────────────────────────────────────────────────────
// ─── 404 ───────────────────────────────────────────────────────────
// S3 + CloudFront serve index.html for every unknown path, so the HTTP
// status is 200 no matter what. The least we can do is stop the page
// masquerading as the home page: keep the wrong URL visible and tell
// crawlers not to index it.
function NotFound() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const titleNodes = Array.from(document.head.querySelectorAll('title'));
    const titleNode = titleNodes.shift() || document.createElement('title');
    titleNode.textContent = 'Page Not Found | Vedic Games';
    if (!titleNode.parentNode) document.head.appendChild(titleNode);
    for (const duplicate of titleNodes) duplicate.remove();

    setSingletonHeadElement('meta[name="robots"]', 'meta', { name: 'robots', content: 'noindex, follow' });
    setSingletonHeadElement('meta[name="description"]', 'meta', {
      name: 'description',
      content: 'That page does not exist on Vedic Games. Browse all free Hindu mythology browser games instead.',
    });
    // A 404 is never the canonical version of anything.
    for (const link of document.head.querySelectorAll('link[rel="canonical"]')) link.remove();
  }, []);

  return (
    <>
      <NavBar />
      <div style={{
        minHeight: '70vh', paddingTop: '96px', textAlign: 'center',
        background: '#0a0a1a', color: '#e8e8f0', fontFamily: FONT.body,
      }}>
        <h1 style={{ color: '#f5c518', fontSize: '38px', margin: '0 0 12px', fontFamily: FONT.heading }}>
          Page Not Found
        </h1>
        <p style={{ opacity: 0.75, margin: '0 0 8px' }}>
          Nothing lives at <code style={{ color: '#f5c518' }}>{location.pathname}</code>.
        </p>
        <p style={{ opacity: 0.75, margin: '0 0 28px' }}>
          The game you are after may have been renamed.
        </p>
        <button
          type="button"
          onClick={() => navigate('/games')}
          style={{
            background: '#f5c518', color: '#0a0a1a', border: 'none', cursor: 'pointer',
            padding: '12px 24px', borderRadius: '6px', fontSize: '15px',
            fontFamily: FONT.heading, letterSpacing: '1px',
          }}
        >
          Browse all games
        </button>
      </div>
      <Footer />
    </>
  );
}

export default function App() {
  const location = useLocation();
  // Game routes get their SEO from GamePage, which knows which game it is.
  // Unknown routes get theirs from NotFound — rendering PageSEO there too
  // would race it and stamp the home page's canonical onto a dead URL.
  const isStaticPage = Object.prototype.hasOwnProperty.call(
    STATIC_PAGE_SEO, location.pathname.replace(/\/+$/, '') || '/',
  );

  return (
    <div>
      {isStaticPage && <PageSEO game={null} />}
      <Routes>
        <Route path="/" element={<><NavBar /><Home /><Footer /></>} />
        <Route path="/games" element={<><NavBar /><GamesPage /><Footer /></>} />
        <Route path="/games/:gameId" element={<GamePage />} />
        <Route path="/games/:gameId/:resultId" element={<GamePage />} />
        <Route path="/ramayana" element={
          <Suspense fallback={<Loading />}>
            <NavBar />
            <div style={{ paddingTop: '56px' }}><RamayanaHub /></div>
            <Footer />
          </Suspense>
        } />
        <Route path="/ramayana/:chapterGameId" element={<RamayanaChapterGamePage />} />
        <Route path="/ramayana-movie" element={<><NavBar /><RamayanaMoviePage /><Footer /></>} />
        <Route path="/about" element={<><NavBar /><AboutPage /><Footer /></>} />
        {/* Redirects old hash/direct slug routes; renders the 404 for the rest */}
        <Route path="*" element={<HashRedirect />} />
      </Routes>
      {/*
        Outside <Routes> so it is present on every page including mid-game.
        The moment a player is annoyed is the moment they will actually tell
        you why, and that moment is almost never on the home page.
      */}
      <FeedbackWidget />
    </div>
  );
}

// ─── Hash Route Redirector (backwards compat) ─────────────────────
function HashRedirect() {
  const navigate = useNavigate();
  const location = useLocation();

  // Recomputed here rather than shared with the effect so the first paint
  // already knows whether a redirect is coming — otherwise a dead URL
  // flashes the loading screen before the 404.
  const isRedirectable = (() => {
    if (location.hash) return true;
    const slug = location.pathname.replace(/^\/+/, '').replace(/\/+$/, '');
    return Boolean(games.find(g => g.id === slug) || GAME_SLUG_ALIASES[slug]);
  })();

  useEffect(() => {
    const hash = window.location.hash.slice(1);
    const directSlug = location.pathname.replace(/^\/+/, '').replace(/\/+$/, '');
    // Redirecting to a bare path throws the query string away, which silently
    // breaks anything carried in it — campaign tags on a shared link, and the
    // engine debug flags the screenshot harness pins the quality tier with.
    const q = location.search || '';
    if (hash) {
      if (hash === 'ramayana') navigate(`/ramayana${q}`, { replace: true });
      else if (hash === 'about') navigate(`/about${q}`, { replace: true });
      else if (hash === 'games') navigate(`/games${q}`, { replace: true });
      else if (games.find(g => g.id === hash) || GAME_SLUG_ALIASES[hash]) navigate(`/games/${GAME_SLUG_ALIASES[hash] || hash}${q}`, { replace: true });
      else navigate(`/${q}`, { replace: true });
    } else if (games.find(g => g.id === directSlug) || GAME_SLUG_ALIASES[directSlug]) {
      navigate(`/games/${GAME_SLUG_ALIASES[directSlug] || directSlug}${q}`, { replace: true });
    }
    // Anything else is genuinely a dead URL. Bouncing it to the home page
    // made every typo look like a successful page load to both users and
    // crawlers; render the 404 in place instead.
  }, [navigate, location]);

  if (isRedirectable) return <Loading />;
  return <NotFound />;
}
